import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import AiAnalysis from './pages/AiAnalysis';
import { Camera, Plus, QrCode, ClipboardList, Home, ArrowLeft, Save, Trash2, Edit, Droplet, Archive, Pill, Activity, BarChart3, AlertTriangle, Settings, Mic, MicOff, CheckSquare, X, LogOut, UserPlus, MessageSquare, Bell, Send, User, Download, Database, Cloud, MapPin, Wind, Thermometer, BookOpen, Search, ExternalLink, Wrench, WifiOff, CalendarClock, Wallet, Package, Map, Radio, Bot } from 'lucide-react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import QRCode from 'react-qr-code';

// --- Components ---

const parseServerDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const v = value.trim();
        if (!v) return null;
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
            return new Date(v.replace(' ', 'T') + 'Z');
        }
        return new Date(v);
    }
    return new Date(value);
};

const TURKISH_QUEEN_RACES = [
    'Anadolu',
    'Kafkas',
    'Karniyol',
    'İtalyan',
    'Muğla',
    'Yığılca',
    'Ege Ekotipi',
    'Melez'
];

const normalizeQueenRaceList = (races = []) => {
    const values = [...races, ...TURKISH_QUEEN_RACES]
        .map(race => typeof race === 'string' ? race.trim() : '')
        .filter(Boolean);
    return [...new Set(values)];
};

const getQueenRaceOptions = (currentValue = '', races = TURKISH_QUEEN_RACES) => {
    const options = normalizeQueenRaceList(races);
    if (currentValue && !options.includes(currentValue)) {
        return [currentValue, ...options];
    }
    return options;
};

const flattenQueenRaceGroups = (groups = []) => {
    const values = [];
    groups.forEach(group => {
        if (!group?.name) return;
        values.push(group.name);
        (group.variants || []).forEach(variant => {
            if (variant?.name) values.push(`${group.name} / ${variant.name}`);
        });
    });
    return normalizeQueenRaceList(values);
};

const findQueenRaceParent = (value, groups = []) => {
    const parentName = String(value || '').split('/')[0].trim();
    return groups.find(group => group.name === parentName) || null;
};

function useQueenRaces() {
    const [queenRaces, setQueenRaces] = useState(TURKISH_QUEEN_RACES);
    const [queenRaceGroups, setQueenRaceGroups] = useState(TURKISH_QUEEN_RACES.map((name, index) => ({ id: `default-${index}`, name, variants: [] })));

    const loadQueenRaces = async () => {
        const res = await axios.get('/api/queen-races');
        const groups = (res.data.data || []).map(race => ({ ...race, variants: race.variants || [] }));
        setQueenRaceGroups(groups);
        setQueenRaces(flattenQueenRaceGroups(groups));
    };

    const addQueenRaceVariant = async (parentRaceId, parentName, name) => {
        const cleanName = String(name || '').trim();
        if (!cleanName) return null;
        const res = await axios.post('/api/queen-race-variants', { parent_race_id: parentRaceId, name: cleanName });
        const value = res.data.value || `${parentName} / ${cleanName}`;
        await loadQueenRaces();
        return value;
    };

    useEffect(() => {
        loadQueenRaces().catch(err => console.error('Queen races could not be loaded', err));
    }, []);

    return { queenRaces, queenRaceGroups, addQueenRaceVariant, reloadQueenRaces: loadQueenRaces };
};

const HANDBOOK_TOPICS = [
    {
        id: 'varroa',
        title: 'Varroa akarı',
        category: 'Hastalık / Parazit',
        risk: 'Yüksek',
        summary: 'Koloniyi zayıflatan, virüs taşıyabilen ve takip edilmediğinde koloni kaybına gidebilen en önemli parazitlerden biridir.',
        symptoms: ['Kanatsız veya zayıf arılar', 'Düzensiz yavru alanı', 'Kovan önünde sürünen arılar', 'Sonbaharda hızlı nüfus düşüşü'],
        checks: ['Pudra şekeri, alkol yıkama veya yapışkan tabla gibi düzenli sayım yöntemleri kullanın.', 'Sonuçları bakım kaydına tarih ve oranla yazın.', 'Aynı arılıktaki kolonileri birlikte değerlendirin.'],
        actions: ['Bölgenizde ruhsatlı ürünleri ve resmi talimatları takip edin.', 'Uygulama zamanını bal hasadı ve yavru durumuna göre planlayın.', 'Direnç riskine karşı aynı etken maddeye sürekli bağlı kalmayın.'],
        source: 'FAO TECA, Cornell Pollinator Network, EPA Varroa resources'
    },
    {
        id: 'american-foulbrood',
        title: 'Amerikan yavru çürüklüğü',
        category: 'Hastalık',
        risk: 'Kritik',
        summary: 'Bulaşıcı ve sporları uzun süre dayanabilen ciddi bir yavru hastalığıdır; şüphede yetkili uzman desteği gerekir.',
        symptoms: ['Parçalı yavru deseni', 'Çökük veya delik kapaklar', 'Kötü koku', 'Kahverengi yapışkan larva kalıntısı'],
        checks: ['Şüpheli çerçeveyi başka kovana taşımayın.', 'Ekipman ve el demiri hijyenini artırın.', 'Koloni fotoğrafı ve notları kaydedin.'],
        actions: ['Yerel resmi arıcılık/veteriner otoritesine danışın.', 'Bulaşık ekipmanı arılık içinde dolaştırmayın.', 'Tedavi veya imha kararını mevzuata göre yürütün.'],
        source: 'FDA honey bee health, University of Georgia Bee Program, Britannica'
    },
    {
        id: 'european-foulbrood',
        title: 'Avrupa yavru çürüklüğü',
        category: 'Hastalık',
        risk: 'Yüksek',
        summary: 'Genellikle larva döneminde görülen, stres ve beslenme zayıflığıyla şiddetlenebilen yavru hastalığıdır.',
        symptoms: ['Açık gözlerde ölen larvalar', 'Düzensiz yavru alanı', 'Sarımsı/kahverengi larva', 'Kolonide gelişme geriliği'],
        checks: ['Besin stoku, ana arı performansı ve koloni gücünü birlikte kontrol edin.', 'Şüpheli çerçeveleri işaretleyip fotoğraflayın.', 'Hastalık ayrımı için uzman görüşü alın.'],
        actions: ['Koloni stresini azaltın ve hijyen uygulayın.', 'Zayıf kolonileri yağmacılığa karşı daraltın.', 'İlaç/doz kararını yetkili öneriye göre verin.'],
        source: 'FAO TECA, University of Georgia Bee Program'
    },
    {
        id: 'nosema',
        title: 'Nosema',
        category: 'Hastalık',
        risk: 'Orta',
        summary: 'Yetişkin arılarda bağırsak kaynaklı sorunlara ve ilkbahar gelişiminde zayıflığa yol açabilir.',
        symptoms: ['Yavaş ilkbahar gelişimi', 'Düşük nüfus', 'Kovan çevresinde dışkılama', 'Ana arı yenileme eğilimi'],
        checks: ['Nem, havalandırma ve kışlatma koşullarını gözden geçirin.', 'Besin stoku ve koloni nüfusunu takip edin.', 'Kesin tanı için laboratuvar/uzman desteği alın.'],
        actions: ['Kuru, havadar ve yeterli besinli kışlatma sağlayın.', 'Eski petekleri planlı yenileyin.', 'Zayıf kolonileri ayrıca izleyin.'],
        source: 'Cornell Pollinator Network, University of Georgia Bee Program'
    },
    {
        id: 'wax-moth',
        title: 'Petek güvesi',
        category: 'Zararlı',
        risk: 'Orta',
        summary: 'Özellikle zayıf kolonilerde ve depolanan peteklerde zarar verir.',
        symptoms: ['Petekte tünel ve ağ yapısı', 'Larva izleri', 'Bozulan kabartılmış petek', 'Zayıf kolonide hızlı petek kaybı'],
        checks: ['Koloni gücünü petek sayısıyla uyumlu tutun.', 'Boş petekleri uygun koşulda depolayın.', 'Zayıf kovanlarda alanı daraltın.'],
        actions: ['Güçlü koloni yönetimi uygulayın.', 'Depo peteklerini düzenli kontrol edin.', 'Aşırı boş alan bırakmayın.'],
        source: 'FAO good beekeeping practices'
    },
    {
        id: 'robbery',
        title: 'Yağmacılık',
        category: 'Davranış / Risk',
        risk: 'Yüksek',
        summary: 'Zayıf kolonilerde bal ve stok kaybına, hastalık taşınmasına ve kargaşaya neden olabilir.',
        symptoms: ['Uçuş deliğinde kavga', 'Hızlı ve düzensiz uçuş', 'Kovan çevresinde ölü arılar', 'Stokların hızla azalması'],
        checks: ['Uçuş deliğini daraltın.', 'Açıkta bal/petek bırakmayın.', 'Bakım süresini kısa tutun.'],
        actions: ['Zayıf koloniyi daraltın ve koruyun.', 'Beslemeyi akşam saatlerinde ve kapalı yapın.', 'Arılık hijyenini artırın.'],
        source: 'Good beekeeping practice guidance'
    },
    {
        id: 'queenless',
        title: 'Ana arı kaybı',
        category: 'Ana Arı',
        risk: 'Yüksek',
        summary: 'Koloninin düzenli yavru üretimini durdurur; geç fark edilirse koloni zayıflar.',
        symptoms: ['Günlük yumurta yokluğu', 'Huzursuz ses', 'Dağınık yavru', 'Yalancı ana belirtileri'],
        checks: ['Yumurta, açık yavru ve kapalı yavru sırasını kontrol edin.', 'Ana memesi var mı bakın.', 'Son kontrol tarihini bakım kaydından karşılaştırın.'],
        actions: ['Uygun dönemde ana arı verme veya birleştirme planlayın.', 'Yalancı ana şüphesinde uzman desteği alın.', 'Kontrolü birkaç gün sonra tekrar edin.'],
        source: 'General beekeeping practice'
    },
    {
        id: 'weak-colony',
        title: 'Zayıf koloni',
        category: 'Koloni Yönetimi',
        risk: 'Orta',
        summary: 'Hastalık, yaşlı ana, besin yetersizliği veya yanlış hacim yönetimi nedeniyle ortaya çıkabilir.',
        symptoms: ['Az arı nüfusu', 'Petekleri saramama', 'Düşük yavru alanı', 'Yağmacılığa açık olma'],
        checks: ['Ana arı, besin stoku, yavru deseni ve hastalık belirtisini birlikte değerlendirin.', 'Kovan hacmini koloni gücüne göre ayarlayın.', 'Açık görev oluşturun.'],
        actions: ['Koloniyi daraltın ve izlemeye alın.', 'Gerekirse destekleme veya birleştirme planlayın.', 'Sebep bulunmadan sadece besleme ile yetinmeyin.'],
        source: 'FAO good beekeeping practices'
    },
    {
        id: 'chalkbrood',
        title: 'Kireç hastalığı',
        category: 'Hastalık',
        risk: 'Orta',
        summary: 'Yavru arılarda mumyalaşma ile görülen mantari bir problemdir; nem ve stresle artabilir.',
        symptoms: ['Beyaz/gri mumya larvalar', 'Uçuş deliği önünde sert kalıntılar', 'Düzensiz yavru alanı'],
        checks: ['Kovan nemi ve havalandırmayı kontrol edin.', 'Ana arı performansını izleyin.', 'Etkilenen çerçeveleri not alın.'],
        actions: ['Nem yönetimini iyileştirin.', 'Koloniyi güçlü tutun.', 'Sürekli tekrar ediyorsa ana arı değişimini değerlendirin.'],
        source: 'General bee disease guidance'
    },
    {
        id: 'spring-care',
        title: 'İlkbahar bakım listesi',
        category: 'Sezon',
        risk: 'Düşük',
        summary: 'Sezon başlangıcında stok, ana arı, yavru ve hastalık kontrolü yapılmalıdır.',
        symptoms: ['Kıştan zayıf çıkan koloni', 'Stok azlığı', 'Düzensiz yavru başlangıcı'],
        checks: ['Besin stoku', 'Ana arı/yumurta', 'Yavru düzeni', 'Varroa ve hastalık belirtisi'],
        actions: ['Zayıf kolonileri izleme listesine alın.', 'Gereksiz kovan hacmini azaltın.', 'Bakım kayıtlarını düzenli girin.'],
        source: 'Good beekeeping practice guidance'
    },
    {
        id: 'summer-care',
        title: 'Yaz ve hasat dönemi',
        category: 'Sezon',
        risk: 'Düşük',
        summary: 'Bal akımı, oğul eğilimi, su ihtiyacı ve hasat hijyeni bu dönemin ana konularıdır.',
        symptoms: ['Sıkışan koloni', 'Oğul memeleri', 'Yetersiz ballık alanı', 'Sıcak stres belirtisi'],
        checks: ['Ballık alanı', 'Oğul memesi', 'Su kaynağı', 'Hasat olgunluğu'],
        actions: ['Yeterli alan sağlayın.', 'Hasat kayıtlarını kg ve tarih ile girin.', 'Bal hijyeni ve nem kontrolüne dikkat edin.'],
        source: 'Good beekeeping practice guidance'
    },
    {
        id: 'wintering',
        title: 'Kışlatma hazırlığı',
        category: 'Sezon',
        risk: 'Yüksek',
        summary: 'Kışa güçlü nüfus, yeterli stok ve düşük hastalık/parazit yüküyle girilmelidir.',
        symptoms: ['Stok azlığı', 'Zayıf nüfus', 'Nemli kovan', 'Sonbaharda varroa baskısı'],
        checks: ['Stok miktarı', 'Kovan havalandırması', 'Ana arı durumu', 'Varroa takibi'],
        actions: ['Zayıf kolonileri birleştirmeyi değerlendirin.', 'Kovanı rüzgar ve nemden koruyun.', 'Sonbahar kontrollerini kayda alın.'],
        source: 'FAO good beekeeping practices'
    },
    {
        id: 'queen-rearing-basic',
        title: 'Ana arı çoğaltma: temel plan',
        category: 'Ana Arı',
        risk: 'Orta',
        summary: 'Ana arı üretiminde amaç, iyi damızlık koloniden çok genç larvaları veya yumurtalı çerçeveyi kullanarak arılara kontrollü ana memesi yaptırmaktır.',
        symptoms: ['Yeni ana ihtiyacı', 'Koloni çoğaltma hedefi', 'Yaşlı veya verimi düşen ana arı', 'Seçilmiş iyi koloniden yavru alma ihtiyacı'],
        checks: ['Damızlık koloni sakin, verimli, hastalıksız ve düzenli yavrulu olmalıdır.', 'Ana arı üretimi için bol genç işçi arı, polen ve nektar/şurup desteği gerekir.', 'Çiftleşme başarısı için mevsim uygun, hava uçuşa elverişli ve erkek arı varlığı yeterli olmalıdır.'],
        actions: ['İlk denemede az sayıda ana memesiyle başlayın.', 'Ana arısız başlatıcı veya güçlü hücre yapıcı koloni hazırlayın.', 'Ana memelerini çıkıştan 1-2 gün önce çiftleşme kutularına veya anasız mini kolonilere verin.', 'Yeni ana yumurta atmaya başlayana kadar kovana gereksiz müdahaleyi azaltın.'],
        source: 'Bee Health Extension, FAO TECA, Penn State Extension, Agriculture Victoria'
    },
    {
        id: 'queen-rearing-grafting',
        title: 'Ana arı üretimi: larva transferi',
        category: 'Ana Arı',
        risk: 'Orta',
        summary: 'Larva transferinde bir günlük çok küçük larvalar damızlık koloniden alınır, ana memesi kaplarına aktarılır ve ana arısız/hazırlanmış hücre yapıcı koloniye verilir.',
        symptoms: ['Birden fazla ana memesi üretme ihtiyacı', 'Damızlık özellikleri seçerek çoğaltma hedefi', 'Takvimli ana arı üretimi yapmak isteme'],
        checks: ['Aktarılacak larva çok genç, küçük ve kurumamış olmalıdır.', 'Transfer sırasında larvalar soğumamalı ve kurutulmamalıdır.', 'Başlatıcı kolonide başka genç larva bırakılırsa arılar istenmeyen acil ana memesi yapabilir.'],
        actions: ['Damızlık çerçeveyi nemli bezle koruyup hızlı çalışın.', 'Transfer edilen kapları bol genç işçi arılı hücre yapıcıya verin.', 'Kapalanan ana memelerini takvime göre ayırın; erken çıkan bir ana diğer memeleri bozabilir.', 'Her parti için tarih, damızlık koloni ve kabul oranını kaydedin.'],
        source: 'Penn State Extension, Bee Health Extension, UC Davis California Master Beekeeper Program'
    },
    {
        id: 'queen-rearing-nuc-method',
        title: 'Basit yöntem: nukleusla ana çoğaltma',
        category: 'Ana Arı',
        risk: 'Düşük',
        summary: 'Küçük bir anasız nukleus; kapalı yavru, genç larva/yumurta, bal-polen ve yeterli arıyla kurularak kendi ana memesini yapmaya yönlendirilebilir.',
        symptoms: ['Aşılama ekipmanı olmadan ana üretmek isteme', 'Az sayıda yeni ana ihtiyacı', 'Koloni bölerek çoğaltma planı'],
        checks: ['Nukleusta genç larva/yumurta bulunmalıdır.', 'Yeterli genç arı ve kapalı yavru yoksa ana memesi kalitesi düşebilir.', 'Aynı arılıkta kurulan nukleusta uçucu arılar eski kovana dönebilir.'],
        actions: ['1-2 çerçeve kapalı yavru, bir çerçeve genç larva/yumurta, bal-polen ve yeterli arıyla nukleus hazırlayın.', 'Gerekirse nukleusu birkaç gün kapalı/serin tutma veya uzak arılığa taşıma planı yapın.', '6. gün civarında gelişen ana memelerini kontrol edin.', 'Yeni ana yumurta gösterene kadar ana kabulünü zorlayacak müdahalelerden kaçının.'],
        source: 'FAO TECA Beekeeping in Africa, Purdue/Penn State Extension'
    },
    {
        id: 'queen-introduction',
        title: 'Yeni ana arı verme',
        category: 'Ana Arı',
        risk: 'Yüksek',
        summary: 'Yeni ana arı verme işleminde koloni önce gerçekten anasız olmalı, işçilerin yeni ana kokusuna alışması için aceleci salım yapılmamalıdır.',
        symptoms: ['Satın alınan veya üretilen ana arıyı kovana verme', 'Yaşlı ana değişimi', 'Anasız koloniyi kurtarma'],
        checks: ['Kolonide eski ana veya açık ana memesi kalmadığını kontrol edin.', 'Yalancı ana belirtisi varsa doğrudan ana verme başarısı düşer.', 'Kolonide yeterli genç arı ve besin olması kabulü kolaylaştırır.'],
        actions: ['Yeni anayı kafesle verin ve işçilerin sakin davranıp davranmadığını izleyin.', 'Şeker hamuru bölmesini erken açmak yerine kademeli kabulü tercih edin.', 'Ana salındıktan sonra ilk yumurta kontrolü için birkaç gün bekleyin.', 'Başarısızlıkta nedeni kaydedin: yalancı ana, açlık, hastalık, eski ana veya aşırı stres.'],
        source: 'Purdue Extension, Penn State Extension, Bee Health Extension'
    },
    {
        id: 'split-colony',
        title: 'Koloni bölme ve çoğaltma',
        category: 'Koloni Yönetimi',
        risk: 'Orta',
        summary: 'Koloni bölme; güçlü koloniden yavru, besin ve arı alarak yeni bir koloni kurma işlemidir. Başarı için mevsim, besin, genç arı ve ana arı planı birlikte düşünülmelidir.',
        symptoms: ['Koloni sayısını artırma hedefi', 'Oğul eğilimini azaltma', 'Güçlü koloniyi dengeleme'],
        checks: ['Bölünecek koloni hastalıksız ve güçlü olmalıdır.', 'Yeni bölmede kapalı yavru, genç arı, bal-polen ve ana arı veya ana memesi planı bulunmalıdır.', 'Bölme sonrası eski ve yeni kolonilerin besin durumu ayrı izlenmelidir.'],
        actions: ['Bölmeyi bal akımı ve hava durumuna göre planlayın.', 'Yeni bölmeye ana memesi, hazır ana veya kendi ana yapması için uygun genç larva verin.', 'Uçucu arı kaybını hesaba katın; gerekirse yer değişimi veya ek arı silkme uygulayın.', 'Bölme tarihini ve ana arı durumunu kayda girin.'],
        source: 'FAO TECA, Agriculture Victoria, general extension guidance'
    },
    {
        id: 'swarm-control',
        title: 'Oğul kontrolü',
        category: 'Koloni Yönetimi',
        risk: 'Orta',
        summary: 'Oğul, koloninin doğal çoğalma davranışıdır; ancak üretim kaybına yol açabilir. Erken belirti takibi ve alan yönetimi önemlidir.',
        symptoms: ['Kovanın aşırı sıkışması', 'Ana memeleri', 'Nektar akımı öncesi hızlı nüfus artışı', 'Petek örme alanının daralması'],
        checks: ['Yavru alanı, ballık alanı ve ana memesi varlığını düzenli kontrol edin.', 'Yaşlı ana ve sıkışık koloni oğul riskini artırabilir.', 'Sadece ana memesi kesmek kalıcı çözüm olmayabilir.'],
        actions: ['Koloniye zamanında alan verin.', 'Güçlü kolonilerde kontrollü bölme veya nukleus alma planlayın.', 'Genç ana kullanımı ve düzenli petek yenileme ile riski azaltın.', 'Oğul belirtisini uygulamada görev veya not olarak işaretleyin.'],
        source: 'FAO TECA, Agriculture Victoria, extension beekeeping guidance'
    },
    {
        id: 'drone-and-mating',
        title: 'Erkek arı ve çiftleşme koşulları',
        category: 'Ana Arı',
        risk: 'Orta',
        summary: 'Yeni ana arının verimli olması, uygun yaşta erkek arı varlığına ve iyi uçuş havasına bağlıdır. Ana üretim takvimi erkek arı durumundan ayrı düşünülmemelidir.',
        symptoms: ['Yeni ana yumurtlamaya geç başlıyor', 'Düzensiz veya erkek ağırlıklı yavru', 'Uygun olmayan mevsimde ana üretimi'],
        checks: ['Arılıkta olgun erkek arı varlığını kontrol edin.', 'Soğuk, yağmurlu veya rüzgarlı dönemlerde çiftleşme başarısı düşebilir.', 'Yeni ana çıktıktan sonra yumurta için makul bekleme süresi tanıyın.'],
        actions: ['Ana üretimini ilkbahar sonu-yaz başı gibi erkek arının bol olduğu döneme denk getirin.', 'Çiftleşme kutularını yeterli arı ve besinle hazırlayın.', 'Yumurta başlangıcını görmeden yeni anayı başarılı kabul etmeyin.', 'Başarısız çiftleşen anaları erken tespit edip değiştirin.'],
        source: 'Bee Health Extension, FAO TECA, Penn State Extension'
    }
];

const HANDBOOK_CATEGORIES = ['Tümü', ...Array.from(new Set(HANDBOOK_TOPICS.map(topic => topic.category)))];
const HANDBOOK_RISKS = ['Tümü', 'Kritik', 'Yüksek', 'Orta', 'Düşük'];

const getRiskClass = (risk) => {
    if (risk === 'Kritik') return 'bg-red-100 text-red-800 border-red-200';
    if (risk === 'Yüksek') return 'bg-orange-100 text-orange-800 border-orange-200';
    if (risk === 'Orta') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-green-100 text-green-800 border-green-200';
};

function Layout({ children }) {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [showProfile, setShowProfile] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [city, setCity] = useState(user?.city || 'Ankara');
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (user?.city) setCity(user.city);
    }, [user]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/auth/profile', { city });
            setMsg('Profil güncellendi!');
            setTimeout(() => { setShowProfile(false); setMsg(''); window.location.reload(); }, 1000); // Reload to update context
        } catch (err) {
            setMsg(err.response?.data?.error || 'Hata oluştu');
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/auth/change-password', { oldPassword, newPassword });
            setMsg('Şifre değiştirildi!');
            setOldPassword('');
            setNewPassword('');
            setTimeout(() => { setShowProfile(false); setMsg(''); }, 1500);
        } catch (err) {
            setMsg(err.response?.data?.error || 'Hata oluştu');
        }
    };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <header className="bg-amber-500 text-white p-4 shadow-md sticky top-0 z-10">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
            <Link to="/" className="text-xl font-bold flex items-center gap-2">
              🐝 KovanTakip
            </Link>
            {user?.username && (
              <span className="inline-flex items-center px-2 py-0.5 bg-amber-600/40 rounded-full text-[10px] sm:text-xs font-bold tracking-wide w-fit">
                {user.username}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
             <span className="font-semibold text-amber-100 text-xs md:text-sm">
                <span className="md:hidden">3B YAZILIM</span>
                <span className="hidden md:inline">3B Bilgisayar Yazılım</span>
             </span>
             <div className="flex items-center gap-2">
                 {user?.username === 'admin' && (
                     <button onClick={() => navigate('/admin')} className="text-xs bg-amber-700 p-1 rounded hover:bg-amber-800">Admin</button>
                 )}
                 <button onClick={() => setShowProfile(true)} className="p-1 hover:bg-amber-600 rounded-full" title="Profil">
                    <User size={20} />
                 </button>
                 <button onClick={logout} className="p-1 hover:bg-amber-600 rounded-full" title="Çıkış Yap">
                    <LogOut size={20} />
                 </button>
             </div>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      {showProfile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 relative">
                  <button onClick={() => setShowProfile(false)} className="absolute top-2 right-2 text-gray-500"><X size={24}/></button>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><User/> Profil Ayarları</h3>
                  <p className="text-sm text-gray-600 mb-4">Kullanıcı: <b>{user.username}</b></p>
                  
                  <form onSubmit={handleUpdateProfile} className="space-y-3 mb-6 border-b pb-6">
                      <h4 className="font-bold text-sm text-gray-700">Genel Bilgiler</h4>
                      <div>
                          <label className="text-xs text-gray-500 block mb-1">Şehir (Hava Durumu İçin)</label>
                          <input 
                              type="text" className="w-full border rounded p-2"
                              value={city} onChange={e => setCity(e.target.value)}
                              placeholder="Örn: Ankara"
                          />
                      </div>
                      <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700">Kaydet</button>
                  </form>

                  <form onSubmit={handleChangePassword} className="space-y-3">
                      <h4 className="font-bold text-sm text-gray-700">Şifre Değiştir</h4>
                      <input 
                          type="password" placeholder="Eski Şifre" className="w-full border rounded p-2"
                          value={oldPassword} onChange={e => setOldPassword(e.target.value)} required
                      />
                      <input 
                          type="password" placeholder="Yeni Şifre" className="w-full border rounded p-2"
                          value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                      />
                      {msg && <p className={`text-xs font-bold ${msg.includes('Hata') ? 'text-red-500' : 'text-green-500'}`}>{msg}</p>}
                      <button type="submit" className="w-full bg-amber-500 text-white py-2 rounded font-bold hover:bg-amber-600">Güncelle</button>
                  </form>
              </div>
          </div>
      )}

      <main className="container mx-auto p-4 pb-20">
        {children}
      </main>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-1.5 flex justify-around shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-20">
        <NavLink to="/" icon={<Home size={22} />} label="Kovanlar" />
        <NavLink to="/scan" icon={<QrCode size={22} />} label="Tara" />
        <NavLink to="/chat" icon={<MessageSquare size={22} />} label="Sohbet" />
        <NavLink to="/handbook" icon={<BookOpen size={22} />} label="El Kitabı" />
        <NavLink to="/tools" icon={<Wrench size={22} />} label="Araçlar" />
        <NavLink to="/new-hive" icon={<Plus size={22} />} label="Ekle" />
      </nav>
    </div>
  );
}

function NavLink({ to, icon, label }) {
  return (
    <Link to={to} className="flex flex-col items-center p-1.5 text-gray-600 hover:text-amber-600 active:text-amber-700 min-w-0">
      {icon}
      <span className="text-[11px] mt-1 leading-none whitespace-nowrap">{label}</span>
    </Link>
  );
}

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    if (!src) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute top-3 right-3">
        <button type="button" className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2" onClick={onClose} aria-label="Kapat">
          <X size={20} />
        </button>
      </div>
      <div className="max-w-[95vw] max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt || 'Görsel'} className="max-w-[95vw] max-h-[92vh] object-contain rounded-lg shadow-2xl" />
      </div>
    </div>
  );
}

function CameraCaptureModal({ open, onClose, onCapture }) {
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const [starting, setStarting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);

  const stopStream = () => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
    }
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    let canceled = false;
    setError(null);
    setStarting(true);
    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: false
    }).then((stream) => {
      if (canceled) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => undefined);
      }
    }).catch((e) => {
      setError(e?.message || 'Kamera açılamadı');
    }).finally(() => {
      setStarting(false);
    });

    return () => {
      canceled = true;
      stopStream();
    };
  }, [open]);

  const handleClose = () => {
    stopStream();
    onClose();
  };

  const handleCapture = async () => {
    if (!videoRef.current) return;
    setCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      const w = video.videoWidth || 1280;
      const h = video.videoHeight || 720;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      if (!blob) throw new Error('Fotoğraf oluşturulamadı');
      onCapture(blob);
      handleClose();
    } catch (e) {
      setError(e?.message || 'Çekim başarısız');
    } finally {
      setCapturing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-bold text-gray-800">Kamera</div>
          <button type="button" className="text-gray-500 hover:text-gray-800" onClick={handleClose} aria-label="Kapat">
            <X size={20} />
          </button>
        </div>
        <div className="relative bg-black aspect-square">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          {(starting || capturing) && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40">
              {starting ? 'Kamera açılıyor...' : 'Çekiliyor...'}
            </div>
          )}
          {error && (
            <div className="absolute bottom-0 left-0 right-0 bg-red-600/90 text-white text-xs p-2">
              {error}
            </div>
          )}
        </div>
        <div className="p-4 flex gap-2">
          <button type="button" className="flex-1 border rounded-lg p-3 font-semibold" onClick={handleClose}>
            İptal
          </button>
          <button type="button" className="flex-1 bg-blue-600 text-white rounded-lg p-3 font-semibold disabled:opacity-50" onClick={handleCapture} disabled={starting || capturing}>
            Çek
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Pages ---

function Dashboard() {
  const { user } = useAuth(); // Auth context'ten user bilgisini al
  const [hives, setHives] = useState([]);
  const [stats, setStats] = useState({ totalHives: 0, totalHoney: 0, weakHives: 0, harvestChart: [], averageHealth: 100, attentionHives: [] });
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  
  // Bulk Actions State
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedHives, setSelectedHives] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    // Sadece kullanıcı giriş yapmışsa veri çek
    if (user) {
        fetchData();
    } else {
        setLoading(false); // Kullanıcı yoksa loading'i kapat ki sonsuz dönmesin
    }
    fetchWeather();
  }, [user]); // user değiştiğinde (giriş yapıldığında) tetikle

  const fetchData = async () => {
    try {
      // Eğer kullanıcı login olmamışsa veya token yoksa hata fırlatabilir, bu catch'e düşer.
      // 401 hatası interceptor tarafından yakalanıp logout'a yönlendirilir.
      const [hivesRes, statsRes, annRes] = await Promise.all([
          axios.get('/api/hives', { validateStatus: status => status >= 200 && status < 400 }),
          axios.get('/api/dashboard/stats', { validateStatus: status => status >= 200 && status < 400 }),
          axios.get('/api/announcements', { validateStatus: status => status >= 200 && status < 400 })
      ]);
      
      // Veri doğrulama
      if (hivesRes?.data?.data) {
          setHives(hivesRes.data.data);
      }
      if (statsRes?.data?.data) {
          setStats(statsRes.data.data);
      }
      if (annRes?.data?.data) {
          setAnnouncements(annRes.data.data);
      }
    } catch (err) {
      console.error("Fetch data error:", err);
      // Hata durumunda state'i temizle veya hata göster
    } finally {
      setLoading(false);
    }
  };

  const exportAllHiveData = async (format = 'xlsx') => {
      try {
          const isXlsx = format === 'xlsx';
          const res = await axios.get(`/api/reports/hives.${isXlsx ? 'xlsx' : 'csv'}`, { responseType: 'blob' });
          const type = isXlsx
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : 'text/csv;charset=utf-8;';
          const url = URL.createObjectURL(new Blob([res.data], { type }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `kovan_raporu_${new Date().toISOString().slice(0, 10)}.${isXlsx ? 'xlsx' : 'csv'}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
      } catch (err) {
          console.error(err);
          alert('Rapor indirilemedi.');
      }
  };

  const fetchWeather = async () => {
      try {
          // Varsayılan: Ankara
          let lat = 39.93;
          let long = 32.85;
          let cityName = 'Ankara';

          // 1) Öncelik: Tarayıcı konumu
          if (navigator.geolocation) {
              try {
                  const position = await new Promise((resolve, reject) => {
                      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 6000 });
                  });
                  lat = position.coords.latitude;
                  long = position.coords.longitude;
                  // Reverse geocoding for Turkish city/district names
                  try {
                      const rev = await axios.get('/api/proxy/reverse-geocoding', {
                          params: { lat, long },
                          validateStatus: s => s >= 200 && s < 400
                      });
                      const place = rev.data?.results?.[0];
                      if (place) {
                          const ilce = place.admin2 || '';
                          const il = place.admin1 || place.name || '';
                          cityName = [ilce, il].filter(Boolean).join(', ');
                      } else {
                          cityName = "Konumunuz";
                      }
                  } catch {
                      cityName = "Konumunuz";
                  }
              } catch (e) {
                  // Konum reddedilirse şehir adına düş
                  // console.log("Konum alınamadı:", e);
              }
          }

          // 2) Konum alınamadıysa kullanıcı şehrini kullan
          if (cityName === 'Ankara' && user?.city) {
              try {
                  const geoRes = await axios.get(`/api/proxy/geocoding`, { params: { name: user.city } });
                  if (geoRes.data?.results?.length > 0) {
                      lat = geoRes.data.results[0].latitude;
                      long = geoRes.data.results[0].longitude;
                      cityName = geoRes.data.results[0].name; // Türkçe karakter desteği
                  } else {
                      cityName = user.city;
                  }
              } catch (e) {
                  // console.error("Geocoding error:", e);
                  cityName = user.city;
              }
          }

          // 3) Hava durumu (Server proxy üzerinden, CORS ve dil uyumlu)
          const res = await axios.get(`/api/proxy/weather`, {
              params: { lat, long },
              validateStatus: s => s >= 200 && s < 400
          });
          if (res.data?.current) {
              setWeather({ ...res.data.current, cityName });
          }
      } catch (err) {
          console.error("Weather fetch error", err);
          setWeather({
              temperature_2m: 20,
              relative_humidity_2m: 50,
              wind_speed_10m: 10,
              weather_code: 1,
              cityName: 'Hava bilgisi yok'
          });
      }
  };

  // Group Hives by Apiary
  const groupedHives = hives.reduce((acc, hive) => {
      const apiary = hive.apiary || 'Diğer / Tanımsız';
      if (!acc[apiary]) acc[apiary] = [];
      acc[apiary].push(hive);
      return acc;
  }, {});

  const toggleSelection = (hiveId) => {
      setSelectedHives(prev => 
          prev.includes(hiveId) 
              ? prev.filter(id => id !== hiveId)
              : [...prev, hiveId]
      );
  };

  const toggleSelectApiary = (apiaryHives) => {
      const allSelected = apiaryHives.every(h => selectedHives.includes(h.id));
      if (allSelected) {
          // Deselect all in this apiary
          setSelectedHives(prev => prev.filter(id => !apiaryHives.find(h => h.id === id)));
      } else {
          // Select all in this apiary
          const newIds = apiaryHives.map(h => h.id).filter(id => !selectedHives.includes(id));
          setSelectedHives(prev => [...prev, ...newIds]);
      }
  };

  const todayActions = React.useMemo(() => {
      const attention = Array.isArray(stats.attentionHives) ? stats.attentionHives : [];
      const overdue = hives
          .filter(hive => Number(hive.health_score ?? 100) < 80 || Number(hive.pending_todos || 0) > 0)
          .sort((a, b) => Number(a.health_score ?? 100) - Number(b.health_score ?? 100))
          .slice(0, 4);
      return {
          critical: attention.filter(hive => hive.health_status === 'kritik').length,
          watch: attention.filter(hive => hive.health_status !== 'kritik').length,
          overdue,
      };
  }, [hives, stats.attentionHives]);

  const seasonalSuggestions = React.useMemo(() => {
      const month = new Date().getMonth() + 1;
      const temp = Number(weather?.temperature_2m);
      const wind = Number(weather?.wind_speed_10m);
      const suggestions = [];

      if ([3, 4, 5].includes(month)) {
          suggestions.push({ title: 'Ilkbahar kontrolu', text: 'Ana ari, yavru duzeni ve koloni gucunu detayli modla kaydet.' });
          suggestions.push({ title: 'Ogul riski', text: 'Guclu kovanlarda meme ve sikisikligi kontrol et.' });
      } else if ([6, 7, 8].includes(month)) {
          suggestions.push({ title: 'Hasat takibi', text: 'Bal hasadini kovan bazli kaydet; raporda verimli kovanlar ayrissin.' });
          suggestions.push({ title: 'Su ve golge', text: 'Sicak gunlerde su kaynagini ve havalandirmayi kontrol et.' });
      } else if ([9, 10].includes(month)) {
          suggestions.push({ title: 'Varroa ve sonbahar bakimi', text: 'Varroa seviyesini detayli kontrolde isaretle, tedavi kaydini unutma.' });
          suggestions.push({ title: 'Kis stoku', text: 'Besin stoku az olan kovanlari gorev olarak isaretle.' });
      } else {
          suggestions.push({ title: 'Kis izleme', text: 'Kovani gereksiz acmadan agirlik, ruzgar ve nem kosullarini izle.' });
      }

      if (Number.isFinite(temp) && temp < 10) {
          suggestions.unshift({ title: 'Soguk hava uyarisi', text: 'Detayli acim yerine dis gozlem ve not kaydi daha uygundur.' });
      }
      if (Number.isFinite(wind) && wind > 25) {
          suggestions.unshift({ title: 'Ruzgarli gun', text: 'Kapak acma ve uzun kontrol islerini ertelemeyi degerlendir.' });
      }

      return suggestions.slice(0, 3);
  }, [weather]);

  if (loading) return <div className="p-4 text-center">Yükleniyor...</div>;

  return (
    <Layout title="Ana Sayfa">
      
      {/* Announcements */}
      {announcements.length > 0 && (
          <div className="mb-4 space-y-2">
              {announcements.map(ann => (
                  <div key={ann.id} className={`p-3 rounded-lg border-l-4 shadow-sm flex items-start gap-3 ${
                      ann.type === 'warning' ? 'bg-orange-50 border-orange-500 text-orange-800' :
                      ann.type === 'success' ? 'bg-green-50 border-green-500 text-green-800' :
                      ann.type === 'danger' ? 'bg-red-50 border-red-500 text-red-800' :
                      'bg-blue-50 border-blue-500 text-blue-800'
                  }`}>
                      <Bell size={20} className="shrink-0 mt-0.5" />
                      <div>
                          <h4 className="font-bold text-sm">{ann.title}</h4>
                          <p className="text-sm opacity-90">{ann.content}</p>
                      </div>
                  </div>
              ))}
          </div>
      )}

      {/* Weather Widget */}
      {weather && (
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg mb-6 relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 opacity-10 transform translate-x-10 -translate-y-10">
                <Cloud size={150} />
            </div>

            <div className="flex justify-between items-start relative z-10">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 mb-1">
                        <MapPin size={20} className="opacity-80"/> {weather.cityName}
                    </h2>
                    <p className="text-blue-100 text-sm">{new Date().toLocaleDateString('tr-TR', {weekday: 'long', day: 'numeric', month: 'long'})}</p>
                </div>
                <div className="text-right">
                     <div className="text-5xl font-bold mb-1">{weather.temperature_2m}°</div>
                     <div className="text-sm font-medium opacity-90 capitalize">
                        {weather.weather_code <= 3 ? 'Açık / Parçalı Bulutlu' : 
                         weather.weather_code <= 48 ? 'Sisli' : 
                         weather.weather_code <= 67 ? 'Yağmurlu' : 
                         weather.weather_code <= 77 ? 'Kar Yağışlı' : 'Fırtına'}
                     </div>
                </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mt-6 border-t border-white/20 pt-4 relative z-10">
                <div className="flex flex-col items-center">
                    <Wind size={20} className="mb-1 opacity-80"/>
                    <span className="font-bold">{weather.wind_speed_10m} <span className="text-xs font-normal opacity-70">km/s</span></span>
                    <span className="text-xs opacity-60">Rüzgar</span>
                </div>
                <div className="flex flex-col items-center border-l border-white/20">
                    <Droplet size={20} className="mb-1 opacity-80"/>
                    <span className="font-bold">%{weather.relative_humidity_2m}</span>
                    <span className="text-xs opacity-60">Nem</span>
                </div>
                <div className="flex flex-col items-center border-l border-white/20">
                    <Thermometer size={20} className="mb-1 opacity-80"/>
                    <span className="font-bold">{weather.apparent_temperature || weather.temperature_2m}°</span>
                    <span className="text-xs opacity-60">Hissedilen</span>
                </div>
            </div>
        </div>
      )}

      {/* Subscription Info Card */}
      <div className="bg-white p-4 rounded-lg shadow mb-4 border-l-4 border-purple-500">
          <div className="flex justify-between items-center">
              <div>
                  <h3 className="font-bold text-gray-800">Abonelik Durumu</h3>
                  <p className="text-sm text-gray-600">
                      {user?.subscription_end 
                          ? `Bitiş: ${new Date(user.subscription_end).toLocaleDateString('tr-TR')}` 
                          : 'Paket: Hobi (Süresiz)'}
                  </p>
              </div>
              <div className="text-right">
                  <div className="text-2xl font-bold text-gray-800">
                      {stats.totalHives} <span className="text-sm text-gray-500 font-normal">/ {user?.max_hives || 5}</span>
                  </div>
                  <div className="text-xs text-gray-500">Kovan Hakkı</div>
              </div>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div 
                  className={`h-2.5 rounded-full ${stats.totalHives >= (user?.max_hives || 5) ? 'bg-red-500' : 'bg-green-500'}`} 
                  style={{ width: `${Math.min((stats.totalHives / (user?.max_hives || 5)) * 100, 100)}%` }}
              ></div>
          </div>
          {stats.totalHives >= (user?.max_hives || 5) && (
              <p className="text-xs text-red-500 mt-1 font-bold">⚠️ Kovan limitine ulaştınız.</p>
          )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
          <div className="bg-white p-3 rounded-lg shadow border-l-4 border-blue-500 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-800">{stats.totalHives}</span>
              <span className="text-xs text-gray-500 text-center">Toplam Kovan</span>
          </div>
          <div className="bg-white p-3 rounded-lg shadow border-l-4 border-amber-500 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-800">{stats.totalHoney.toFixed(1)}</span>
              <span className="text-xs text-gray-500 text-center">Hasat (Kg)</span>
          </div>
          <div className="bg-white p-3 rounded-lg shadow border-l-4 border-red-500 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-800">{stats.weakHives}</span>
              <span className="text-xs text-gray-500 text-center">Zayıf / İlgi Bekleyen</span>
          </div>
          <div className="bg-white p-3 rounded-lg shadow border-l-4 border-purple-500 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-800">{stats.pendingTodos || 0}</span>
              <span className="text-xs text-gray-500 text-center">Yapılacaklar</span>
          </div>
      </div>

      <div className="bg-white p-3 rounded-lg shadow border-l-4 border-green-500 flex items-center justify-between mb-6">
          <div>
              <span className="text-xs text-gray-500">Ortalama Saglik</span>
              <div className="text-2xl font-bold text-gray-800">{Number(stats.averageHealth || 100).toFixed(0)}/100</div>
          </div>
          <div className="flex items-center gap-2">
              <button
                  type="button"
                  onClick={() => exportAllHiveData('xlsx')}
                  className="text-xs font-bold text-green-700 bg-green-50 border border-green-100 px-3 py-2 rounded flex items-center gap-1"
              >
                  <Download size={14} /> XLSX Rapor
              </button>
              <button
                  type="button"
                  onClick={() => exportAllHiveData('csv')}
                  className="text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2 rounded"
              >
                  CSV
              </button>
          </div>
      </div>

      {stats.attentionHives && stats.attentionHives.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-red-400">
              <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-500" /> Ilgi Bekleyen Kovanlar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {stats.attentionHives.map(hive => {
                      const hm = getHealthMeta(hive.health_score, hive.health_status);
                      return (
                          <Link key={hive.id} to={`/hive/${hive.id}`} className="border rounded-lg p-3 hover:bg-gray-50 transition">
                              <div className="flex items-center justify-between gap-2">
                                  <span className="font-bold text-gray-800">{hive.name}</span>
                                  <span className={`text-[11px] border px-2 py-0.5 rounded-full ${hm.className}`}>
                                      {hive.health_score}/100
                                  </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{hive.attention_reason || 'Kontrol onerilir'}</p>
                          </Link>
                      );
                  })}
              </div>
          </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <CheckSquare size={16} className="text-blue-500" /> Bugun Ne Yapilacak?
              </h3>
              <span className="text-[11px] text-gray-500">
                  {todayActions.critical} kritik, {todayActions.watch} takip
              </span>
          </div>
          {todayActions.overdue.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {todayActions.overdue.map(hive => {
                      const hm = getHealthMeta(hive.health_score, hive.health_status);
                      return (
                          <div key={hive.id} className="border rounded-lg p-3">
                              <div className="flex items-center justify-between gap-2">
                                  <Link to={`/hive/${hive.id}`} className="font-bold text-gray-800 hover:text-amber-600">{hive.name}</Link>
                                  <span className={`text-[11px] border px-2 py-0.5 rounded-full ${hm.className}`}>{hive.health_score ?? 100}/100</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{hive.attention_reason || `${hive.pending_todos || 0} acik gorev`}</p>
                              <div className="grid grid-cols-2 gap-2 mt-3">
                                  <Link to={`/hive/${hive.id}/log`} className="text-center text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 rounded py-2">
                                      Bakim Ekle
                                  </Link>
                                  <Link to={`/hive/${hive.id}`} className="text-center text-xs font-bold bg-gray-50 text-gray-700 border border-gray-100 rounded py-2">
                                      Detay
                                  </Link>
                              </div>
                          </div>
                      );
                  })}
              </div>
          ) : (
              <div className="text-sm text-gray-500 bg-green-50 border border-green-100 rounded-lg p-3">
                  Bugun acil takip gerektiren kovan gorunmuyor.
              </div>
          )}
      </div>

      {seasonalSuggestions.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-amber-400">
              <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Activity size={16} className="text-amber-500" /> Sezonluk Oneriler
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {seasonalSuggestions.map((item, idx) => (
                      <div key={idx} className="bg-amber-50/60 border border-amber-100 rounded-lg p-3">
                          <div className="font-bold text-sm text-amber-800">{item.title}</div>
                          <p className="text-xs text-gray-600 mt-1">{item.text}</p>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* Harvest Chart (Simple CSS Bars) */}
      {stats.harvestChart && stats.harvestChart.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow mb-6">
              <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <BarChart3 size={16} /> Son 6 Ay Hasat (Kg)
              </h3>
              <div className="flex items-end gap-2 h-32 border-b border-gray-200 pb-1">
                  {stats.harvestChart.map((item, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full group">
                          <div 
                              className="w-full bg-amber-400 rounded-t hover:bg-amber-500 transition-all relative"
                              style={{ height: `${Math.min((item.total / 50) * 100, 100)}%` }} // Scale to max 50kg roughly
                          >
                              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-bold opacity-0 group-hover:opacity-100 transition">{item.total}</span>
                          </div>
                          <span className="text-[10px] text-gray-500 mt-1">{item.month.split('-')[1]}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* Bulk Action Controls */}
      <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-gray-700">Kovan Listesi</h2>
          <button 
              onClick={() => {
                  setIsSelectionMode(!isSelectionMode);
                  setSelectedHives([]);
              }}
              className={`px-3 py-1 rounded text-sm font-bold flex items-center gap-1 transition ${isSelectionMode ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}
          >
              {isSelectionMode ? <><X size={16}/> İptal</> : <><CheckSquare size={16}/> Toplu İşlem</>}
          </button>
      </div>
      
      {Object.keys(groupedHives).length === 0 && (
          <div className="text-center py-10 text-gray-500">
            Henüz kovan eklenmemiş.
          </div>
      )}

      {Object.entries(groupedHives).map(([apiaryName, apiaryHives]) => (
          <div key={apiaryName} className="mb-6">
              <div className="flex items-center justify-between mb-2 pl-1 border-l-4 border-gray-300">
                  <h3 className="text-md font-bold text-gray-600 flex items-center gap-2">
                      <Home size={16} /> {apiaryName} <span className="text-xs font-normal bg-gray-200 px-2 rounded-full">{apiaryHives.length}</span>
                  </h3>
                  {isSelectionMode && (
                      <button 
                          onClick={() => toggleSelectApiary(apiaryHives)}
                          className="text-xs text-blue-500 font-bold px-2"
                      >
                          {apiaryHives.every(h => selectedHives.includes(h.id)) ? 'Seçimi Kaldır' : 'Hepsini Seç'}
                      </button>
                  )}
              </div>
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {apiaryHives.map(hive => {
                    const m = getLastMaintenanceMeta(hive.last_inspection_date, hive.last_inspection_at);
                    const hm = getHealthMeta(hive.health_score, hive.health_status);
                    return (
                        <div key={hive.id} className="relative group">
                            <Link to={`/hive/${hive.id}`} className={`block bg-white p-4 rounded-lg shadow hover:shadow-md transition border-l-4 ${isSelectionMode && selectedHives.includes(hive.id) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-amber-400'} flex justify-between items-center relative overflow-hidden`}>
                                <div>
                                <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                                    {hive.name}
                                    {(hive.queen_year || hive.queen_race) && (
                                        <span className="text-xs bg-purple-50 border border-purple-100 text-purple-700 px-2 py-0.5 rounded-full max-w-[12rem] truncate">
                                            👑 {hive.queen_race || 'Belirsiz'}
                                        </span>
                                    )}
                                    {hive.queen_year && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full text-white ${getQueenColorClass(Number(hive.queen_year))}`}>
                                            {hive.queen_year}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-gray-500 text-sm">{hive.location || 'Konum yok'}</p>
                                <div className="mt-1 flex items-center gap-2">
                                    <span className={`text-[11px] border px-2 py-0.5 rounded-full ${m.className}`}>
                                        {m.label}
                                    </span>
                                    {m.dateText && <span className="text-[11px] text-gray-400">{m.dateText}</span>}
                                </div>
                                <div className="mt-2">
                                    <div className="flex items-center justify-between text-[11px] mb-1">
                                        <span className={`border px-2 py-0.5 rounded-full ${hm.className}`}>Saglik: {hive.health_score ?? 100}/100</span>
                                        {hive.attention_reason && <span className="text-gray-400 truncate max-w-[9rem]">{hive.attention_reason}</span>}
                                    </div>
                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${hm.barClassName}`} style={{ width: `${Math.max(0, Math.min(100, Number(hive.health_score ?? 100)))}%` }}></div>
                                    </div>
                                </div>
                                </div>
                                <div className="text-gray-400">
                                <ClipboardList size={20} />
                                </div>
                            </Link>
                            {!isSelectionMode && (
                                <div className="grid grid-cols-3 gap-2 bg-white border-t border-gray-100 px-3 py-2 rounded-b-lg shadow -mt-1">
                                    <Link to={`/hive/${hive.id}/log`} className="text-center text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded py-2">
                                        Bakim
                                    </Link>
                                    <Link to={`/hive/${hive.id}`} className="text-center text-[11px] font-bold text-gray-700 bg-gray-50 border border-gray-100 rounded py-2">
                                        Detay
                                    </Link>
                                    <Link to="/scan" className="text-center text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded py-2">
                                        Tara
                                    </Link>
                                </div>
                            )}
                            {isSelectionMode && (
                                <div 
                                    onClick={(e) => { e.preventDefault(); toggleSelection(hive.id); }}
                                    className="absolute inset-0 z-10 cursor-pointer flex items-center justify-end pr-4 bg-white/10"
                                >
                                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${selectedHives.includes(hive.id) ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'}`}>
                                        {selectedHives.includes(hive.id) && <CheckSquare size={16} className="text-white" />}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                  })}
              </div>
          </div>
      ))}

      {/* Floating Action Button for Bulk Actions */}
      {isSelectionMode && selectedHives.length > 0 && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-20">
              <button 
                  onClick={() => setShowBulkModal(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg font-bold flex items-center gap-2 hover:bg-blue-700 transition animate-bounce-subtle"
              >
                  <CheckSquare size={20} />
                  {selectedHives.length} Kovanı İşle
              </button>
          </div>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
          <BulkActionModal 
              count={selectedHives.length} 
              hiveIds={selectedHives}
              onClose={() => setShowBulkModal(false)} 
              onSuccess={() => {
                  setShowBulkModal(false);
                  setIsSelectionMode(false);
                  setSelectedHives([]);
                  fetchData(); // Refresh stats if needed
              }}
          />
      )}

    </Layout>
  );
}

function BulkActionModal({ count, hiveIds, onClose, onSuccess }) {
    const [tab, setTab] = useState('feeding'); // feeding, treatment, inspection
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');
    
    // Feeding
    const [feedType, setFeedType] = useState('Şerbet');
    const [feedAmount, setFeedAmount] = useState('');
  
    // Treatment
    const [treatmentType, setTreatmentType] = useState('Varroa');
    
    // Inspection
    const [condition, setCondition] = useState('Normal');

    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const payload = {
            hive_ids: hiveIds,
            date,
            type: tab,
            notes
        };

        if (tab === 'feeding') {
            payload.sub_type = feedType;
            payload.quantity = feedAmount;
        } else if (tab === 'treatment') {
            payload.sub_type = treatmentType;
        } else if (tab === 'inspection') {
            payload.condition = condition;
        }

        try {
            await axios.post('/api/inspections/bulk', payload);
            alert(`${count} kovan başarıyla güncellendi.`);
            onSuccess();
        } catch (err) {
            alert('Hata: ' + (err.response?.data?.error || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
                    <h3 className="font-bold text-lg">{count} Kovan İçin Toplu İşlem</h3>
                    <button onClick={onClose}><X size={24} /></button>
                </div>
                
                <div className="flex border-b">
                    <button onClick={() => setTab('feeding')} className={`flex-1 py-3 text-sm font-medium ${tab === 'feeding' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}>Besleme</button>
                    <button onClick={() => setTab('treatment')} className={`flex-1 py-3 text-sm font-medium ${tab === 'treatment' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}>İlaç</button>
                    <button onClick={() => setTab('inspection')} className={`flex-1 py-3 text-sm font-medium ${tab === 'inspection' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-500'}`}>Kontrol</button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Tarih</label>
                        <input type="date" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={date} onChange={e => setDate(e.target.value)} />
                    </div>

                    {tab === 'feeding' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Besin</label>
                                <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={feedType} onChange={e => setFeedType(e.target.value)}>
                                    <option>Şerbet</option>
                                    <option>Kek</option>
                                    <option>Fondant</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Miktar (Lt/Kg)</label>
                                <input type="number" step="0.1" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={feedAmount} onChange={e => setFeedAmount(e.target.value)} placeholder="0.0" />
                            </div>
                        </div>
                    )}

                    {tab === 'treatment' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">İlaç / Tedavi Türü</label>
                            <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={treatmentType} onChange={e => setTreatmentType(e.target.value)} placeholder="Örn: Oksalik Asit" />
                        </div>
                    )}

                    {tab === 'inspection' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Genel Durum</label>
                            <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={condition} onChange={e => setCondition(e.target.value)}>
                                <option>Normal</option>
                                <option>Zayıf</option>
                                <option>Güçlü</option>
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Ortak Not</label>
                        <textarea 
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                            rows="2"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Tüm seçili kovanlara eklenecek not..."
                        ></textarea>
                    </div>

                    <button disabled={submitting} type="submit" className="w-full bg-blue-600 text-white p-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
                        <CheckSquare size={20} />
                        {submitting ? 'İşleniyor...' : 'Toplu Kaydet'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// Helper for Queen Color
const getQueenColorClass = (year) => {
    const lastDigit = year % 10;
    if ([0, 5].includes(lastDigit)) return 'bg-blue-500'; // Mavi
    if ([1, 6].includes(lastDigit)) return 'bg-gray-400'; // Beyaz (Gri temsili)
    if ([2, 7].includes(lastDigit)) return 'bg-yellow-500'; // Sarı
    if ([3, 8].includes(lastDigit)) return 'bg-red-500'; // Kırmızı
    if ([4, 9].includes(lastDigit)) return 'bg-green-500'; // Yeşil
    return 'bg-gray-500';
};

const getLastMaintenanceMeta = (lastInspectionDate, lastInspectionAt) => {
    if (!lastInspectionDate) {
        return { label: 'Son bakım: yok', dateText: null, className: 'bg-gray-100 text-gray-600 border-gray-200' };
    }

    const parseServerUtcDatetimeToLocal = (value) => {
        if (!value) return null;
        const raw = String(value);
        const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
        const isoUtc = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
        const d = new Date(isoUtc);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const dAtLocal = parseServerUtcDatetimeToLocal(lastInspectionAt);
    let dStart;
    if (dAtLocal) {
        dStart = new Date(dAtLocal.getFullYear(), dAtLocal.getMonth(), dAtLocal.getDate());
    } else {
        const rawDate = String(lastInspectionDate);
        const dateIso = rawDate.length >= 10 ? rawDate.slice(0, 10) : rawDate;
        dStart = new Date(`${dateIso}T00:00:00`);
    }
    if (Number.isNaN(dStart.getTime())) return { label: 'Son bakım: yok', dateText: null, className: 'bg-gray-100 text-gray-600 border-gray-200' };

    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.max(0, Math.floor((todayStart - dStart) / (24 * 60 * 60 * 1000)));

    let rel = '';
    if (diffDays === 0) rel = 'Bugün';
    else if (diffDays === 1) rel = 'Dün';
    else rel = `${diffDays} gün önce`;

    const className = diffDays > 14
        ? 'bg-red-50 text-red-700 border-red-200'
        : diffDays > 7
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-green-50 text-green-700 border-green-200';

    const timeText = dAtLocal ? dAtLocal.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : null;
    const dateText = timeText ? `${dStart.toLocaleDateString('tr-TR')} ${timeText}` : dStart.toLocaleDateString('tr-TR');
    return { label: `Son bakım: ${rel}`, dateText, className };
};

const getHealthMeta = (score, status) => {
    const normalized = Number.isFinite(Number(score)) ? Number(score) : 100;
    const effectiveStatus = status || (normalized >= 80 ? 'iyi' : normalized >= 55 ? 'izle' : 'kritik');
    if (effectiveStatus === 'kritik') {
        return { label: 'Kritik', className: 'bg-red-50 text-red-700 border-red-200', barClassName: 'bg-red-500' };
    }
    if (effectiveStatus === 'izle') {
        return { label: 'Izle', className: 'bg-amber-50 text-amber-700 border-amber-200', barClassName: 'bg-amber-500' };
    }
    return { label: 'Iyi', className: 'bg-green-50 text-green-700 border-green-200', barClassName: 'bg-green-500' };
};

function NewHive() {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [apiary, setApiary] = useState('');
  const [qrCode, setQrCode] = useState('');
  
  // New Fields
  const [queenYear, setQueenYear] = useState(new Date().getFullYear());
  const [queenRace, setQueenRace] = useState('');
  const [queenStatus, setQueenStatus] = useState('Verimli');
  const [notes, setNotes] = useState('');
  const [customQueenRace, setCustomQueenRace] = useState('');
  const { queenRaceGroups, addQueenRaceVariant } = useQueenRaces();

  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use provided QR code or generate one
      const finalQrCode = qrCode || `HIVE-${Date.now()}`;
      
      await axios.post('/api/hives', { 
          name, 
          location, 
          apiary,
          qr_code: finalQrCode,
          queen_year: queenYear,
          queen_race: queenRace,
          queen_status: queenStatus,
          notes 
      });
      navigate('/');
    } catch (err) {
      alert('Hata oluştu: ' + err.message);
    }
  };

  const handleScan = (code) => {
      setQrCode(code);
      setShowScanner(false);
  };

  const handleAddCustomQueenRace = async () => {
      const name = customQueenRace.trim();
      if (!name) return;
      const parent = findQueenRaceParent(queenRace, queenRaceGroups);
      if (!parent || String(parent.id).startsWith('default-')) {
          alert('Önce alt türün bağlı olduğu ana arı cinsini seçin.');
          return;
      }
      try {
          const value = await addQueenRaceVariant(parent.id, parent.name, name);
          setQueenRace(value);
          setCustomQueenRace('');
      } catch (err) {
          alert('Arı cinsi eklenemedi: ' + (err.response?.data?.error || err.message));
      }
  };

  return (
    <Layout title="Yeni Kovan">
      <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Kovan Ekle</h2>
        
        {showScanner ? (
            <div className="mb-4 p-4 bg-gray-100 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold">Barkod Tara</h3>
                    <button type="button" onClick={() => setShowScanner(false)} className="text-red-500">Kapat</button>
                </div>
                <div className="aspect-square bg-black rounded-lg overflow-hidden relative">
                     <Scanner onScan={handleScan} />
                </div>
            </div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Kovan Numarası / Adı</label>
                <input 
                type="text" 
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring focus:ring-amber-200 p-2 border"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Örn: 101 veya Kovan-A"
                />
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">Barkod / QR Kod</label>
                <div className="flex gap-2">
                    <input 
                    type="text" 
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring focus:ring-amber-200 p-2 border"
                    value={qrCode}
                    onChange={e => setQrCode(e.target.value)}
                    placeholder="Boş bırakılırsa otomatik üretilir"
                    />
                    <button 
                        type="button"
                        onClick={() => setShowScanner(true)}
                        className="mt-1 p-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
                        title="Barkod Tara"
                    >
                        <QrCode size={20} />
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mevcut bir etiket varsa okutabilir veya elle girebilirsiniz.</p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Konum</label>
                <input 
                type="text" 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring focus:ring-amber-200 p-2 border"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="Örn: Bahçe Girişi"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Arılık / Bölge</label>
                <input 
                type="text" 
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring focus:ring-amber-200 p-2 border"
                value={apiary}
                onChange={e => setApiary(e.target.value)}
                placeholder="Örn: Yayla, Sahil (Gruplama için)"
                />
            </div>

            {/* Queen Info */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100 space-y-3">
                <h3 className="font-bold text-purple-800 text-sm flex items-center gap-2">
                    👑 Ana Arı Bilgileri
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-purple-700">Doğum Yılı</label>
                        <input 
                            type="number" 
                            className="mt-1 block w-full rounded-md border-purple-200 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-200 p-2 border text-sm"
                            value={queenYear}
                            onChange={e => setQueenYear(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-purple-700">Irkı</label>
                        <select
                            className="mt-1 block w-full rounded-md border-purple-200 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-200 p-2 border text-sm"
                            value={queenRace}
                            onChange={e => setQueenRace(e.target.value)}
                        >
                            <option value="">Arı cinsi seçiniz</option>
                            {queenRaceGroups.map(race => (
                                <optgroup key={race.id || race.name} label={race.name}>
                                    <option value={race.name}>{race.name}</option>
                                    {(race.variants || []).map(variant => (
                                        <option key={variant.id || variant.name} value={`${race.name} / ${variant.name}`}>{variant.name}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <div className="mt-2 flex gap-2">
                            <input
                                type="text"
                                className="min-w-0 flex-1 rounded-md border-purple-200 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-200 p-2 border text-sm"
                                value={customQueenRace}
                                onChange={e => setCustomQueenRace(e.target.value)}
                                placeholder="Seçili cinsin alt türü"
                            />
                            <button
                                type="button"
                                onClick={handleAddCustomQueenRace}
                                className="shrink-0 rounded-md bg-purple-600 px-3 py-2 text-sm font-bold text-white hover:bg-purple-700"
                            >
                                Ekle
                            </button>
                        </div>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-purple-700">Durumu</label>
                    <select 
                        className="mt-1 block w-full rounded-md border-purple-200 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-200 p-2 border text-sm"
                        value={queenStatus}
                        onChange={e => setQueenStatus(e.target.value)}
                    >
                        <option value="Verimli">Verimli</option>
                        <option value="Zayıf">Zayıf</option>
                        <option value="Meme Dikti">Meme Dikti</option>
                        <option value="Yok">Yok / Kesildi</option>
                        <option value="Bilinmiyor">Bilinmiyor</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Notlar</label>
                <textarea 
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring focus:ring-amber-200 p-2 border"
                    rows="3"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Kovan hakkında genel notlar..."
                ></textarea>
            </div>

            <button type="submit" className="w-full bg-amber-500 text-white p-3 rounded-lg font-bold hover:bg-amber-600 transition flex items-center justify-center gap-2">
                <Save size={20} /> Kaydet
            </button>
            </form>
        )}
      </div>
    </Layout>
  );
}

// Internal scanner component for NewHive
function Scanner({ onScan }) {
    const codeReaderRef = React.useRef(new BrowserMultiFormatReader());
    const isScanningRef = React.useRef(true);
    const [status, setStatus] = useState("Kamera açılıyor...");
    const videoRef = React.useRef(null);

    useEffect(() => {
        const codeReader = codeReaderRef.current;
        isScanningRef.current = true;
        
        codeReader.listVideoInputDevices().then((videoInputDevices) => {
            if (videoInputDevices.length === 0) {
                setStatus("Kamera bulunamadı");
                return;
            }
            const backCamera = videoInputDevices.find(device => 
                device.label.toLowerCase().includes('back') || 
                device.label.toLowerCase().includes('environment') || 
                device.label.toLowerCase().includes('arka')
            );
            const selectedDeviceId = backCamera ? backCamera.deviceId : videoInputDevices[0].deviceId;
            
            if (videoRef.current) {
                codeReader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (result) => {
                    if (result && isScanningRef.current) {
                        isScanningRef.current = false;
                        onScan(result.getText());
                    }
                }).catch(err => {
                    if (err && err.name === 'NotAllowedError') {
                        setStatus("Kamera izni reddedildi.");
                    }
                });
            }
        }).catch(err => setStatus("Hata: " + err.message));

        return () => {
            isScanningRef.current = false;
            codeReader.reset();
        };
    }, []);

    return (
        <>
            <video ref={videoRef} className="w-full h-full object-cover"></video>
            <div className="absolute inset-0 border-4 border-amber-500/50 pointer-events-none"></div>
            <div className="absolute bottom-2 left-0 w-full text-white text-xs bg-black/50 p-1 text-center">{status}</div>
        </>
    );
}

function HiveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hive, setHive] = useState(null);
  const [logs, setLogs] = useState([]);
  const [todos, setTodos] = useState([]); // Todos
  const [loading, setLoading] = useState(true);
  
  // Tabs
  const [activeTab, setActiveTab] = useState('history'); // history, gallery, todos
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Gallery photos derived from logs (memoized, always defined)
  const galleryPhotos = React.useMemo(() => {
    try {
      return (Array.isArray(logs) ? logs : [])
        .filter(l => l && l.photo_url)
        .map(l => ({ url: l.photo_url, date: l.date, id: l.id }));
    } catch {
      return [];
    }
  }, [logs]);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [customQueenRace, setCustomQueenRace] = useState('');
  const { queenRaces, queenRaceGroups, addQueenRaceVariant } = useQueenRaces();

  // Todo Form
  const [newTodo, setNewTodo] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [hiveRes, logsRes, todosRes] = await Promise.all([
        axios.get(`/api/hives/${id}`),
        axios.get(`/api/hives/${id}/inspections`),
        axios.get(`/api/hives/${id}/todos`)
      ]);
      setHive(hiveRes.data.data);
      setEditForm(hiveRes.data.data); // Initialize edit form
      setLogs(logsRes.data.data);
      setTodos(todosRes.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteHive = async () => {
    if (window.confirm('Bu kovanı ve tüm geçmişini silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`/api/hives/${id}`);
        navigate('/');
      } catch (err) {
        alert('Silme hatası: ' + err.message);
      }
    }
  };

  const exportHiveData = () => {
      // Simple CSV Export
      if (!logs || logs.length === 0) return alert("Dışa aktarılacak kayıt yok.");
      
      let csv = "Tarih,Tip,Alt Tip,Miktar,Notlar\n";
      logs.forEach(log => {
          csv += `${new Date(log.date).toLocaleDateString()},${log.type},${log.sub_type || '-'},${log.quantity || '-'},"${log.notes || ''}"\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `kovan_${hive.name}_kayitlari.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const updateHive = async (e) => {
      e.preventDefault();
      try {
          await axios.put(`/api/hives/${id}`, editForm);
          setHive(editForm);
          setIsEditing(false);
          alert('Kovan bilgileri güncellendi.');
      } catch (err) {
          alert('Güncelleme hatası: ' + err.message);
      }
  };

  const handleAddCustomQueenRace = async () => {
      const name = customQueenRace.trim();
      if (!name) return;
      const parent = findQueenRaceParent(editForm.queen_race, queenRaceGroups);
      if (!parent || String(parent.id).startsWith('default-')) {
          alert('Önce alt türün bağlı olduğu ana arı cinsini seçin.');
          return;
      }
      try {
          const value = await addQueenRaceVariant(parent.id, parent.name, name);
          setEditForm(prev => ({ ...prev, queen_race: value }));
          setCustomQueenRace('');
      } catch (err) {
          alert('Arı cinsi eklenemedi: ' + (err.response?.data?.error || err.message));
      }
  };

  const deleteLog = async (logId) => {
    if (window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`/api/inspections/${logId}`);
        loadData(); // Reload logs
      } catch (err) {
        alert('Silme hatası: ' + err.message);
      }
    }
  };

  const deleteLogPhoto = async (logId) => {
    if (!window.confirm('Bu görsel silinsin mi?')) return;
    try {
      await axios.delete(`/api/inspections/${logId}/photo`);
      setLogs(prev => (Array.isArray(prev) ? prev.map(l => (l?.id === logId ? { ...l, photo_url: null } : l)) : prev));
    } catch (err) {
      alert('Görsel silme hatası: ' + err.message);
    }
  };

  // Todo Actions
  const addTodo = async (e) => {
      e.preventDefault();
      if (!newTodo.trim()) return;
      try {
          await axios.post('/api/todos', { hive_id: id, text: newTodo, due_date: null });
          setNewTodo('');
          loadData();
      } catch (err) {
          alert('Hata: ' + err.message);
      }
  };

  const toggleTodo = async (todoId) => {
      try {
          await axios.put(`/api/todos/${todoId}/toggle`);
          loadData();
      } catch (err) { console.error(err); }
  };

  const deleteTodo = async (todoId) => {
      if (!window.confirm("Silinsin mi?")) return;
      try {
          await axios.delete(`/api/todos/${todoId}`);
          loadData();
      } catch (err) { console.error(err); }
  };

  // Group logs by type
  const groupLogsByType = () => {
      const groups = {
          'İlaçlama': [],
          'Besleme': [],
          'Kontrol': [],
          'Hasat': []
      };

      logs.forEach(log => {
          let groupName = 'Kontrol';
          if (log.type === 'treatment') groupName = 'İlaçlama';
          else if (log.type === 'feeding') groupName = 'Besleme';
          else if (log.type === 'harvest') groupName = 'Hasat';
          
          groups[groupName].push(log);
      });

      return groups;
  };

  const logGroups = groupLogsByType();

  if (loading) return <div className="p-4 text-center">Yükleniyor...</div>;
  if (!hive) return <div className="p-4 text-center">Kovan bulunamadı.</div>;

  return (
    <Layout title={hive.name}>
      <div className="space-y-6">
        
        {/* Hive Info / Edit Card */}
        <div className="bg-white p-4 rounded-lg shadow relative">
          <div className="absolute top-4 right-4 flex gap-2">
            <button onClick={exportHiveData} className="text-gray-500 hover:text-green-500 p-2" title="Kayıtları İndir (Excel/CSV)">
                <Download size={20} />
            </button>
            <button 
                onClick={() => setIsEditing(!isEditing)}
                className="text-gray-500 hover:text-blue-500 p-2"
                title="Düzenle"
            >
                {isEditing ? <ArrowLeft size={20} /> : <Settings size={20} />}
            </button>
          </div>

          {isEditing ? (
              <form onSubmit={updateHive} className="space-y-3 mt-8">
                  <div>
                      <label className="text-xs text-gray-500">Kovan Adı</label>
                      <input 
                          type="text" className="w-full border rounded p-2"
                          value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
                      />
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Konum</label>
                      <input 
                          type="text" className="w-full border rounded p-2"
                          value={editForm.location} onChange={e => setEditForm({...editForm, location: e.target.value})}
                      />
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Arılık / Bölge</label>
                      <input 
                          type="text" className="w-full border rounded p-2"
                          value={editForm.apiary || ''} onChange={e => setEditForm({...editForm, apiary: e.target.value})}
                          placeholder="Yayladaki Arılık"
                      />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                      <div>
                          <label className="text-xs text-gray-500">Ana Arı Yılı</label>
                          <input 
                              type="number" className="w-full border rounded p-2"
                              value={editForm.queen_year || ''} onChange={e => setEditForm({...editForm, queen_year: e.target.value})}
                              placeholder="Örn: 2024"
                          />
                      </div>
                      <div>
                          <label className="text-xs text-gray-500">Irkı</label>
                          <select
                              className="w-full border rounded p-2"
                              value={editForm.queen_race || ''} onChange={e => setEditForm({...editForm, queen_race: e.target.value})}
                          >
                              <option value="">Arı cinsi seçiniz</option>
                              {editForm.queen_race && !queenRaces.includes(editForm.queen_race) && (
                                  <option value={editForm.queen_race}>{editForm.queen_race}</option>
                              )}
                              {queenRaceGroups.map(race => (
                                  <optgroup key={race.id || race.name} label={race.name}>
                                      <option value={race.name}>{race.name}</option>
                                      {(race.variants || []).map(variant => (
                                          <option key={variant.id || variant.name} value={`${race.name} / ${variant.name}`}>{variant.name}</option>
                                      ))}
                                  </optgroup>
                              ))}
                          </select>
                          <div className="mt-2 flex gap-2">
                              <input
                                  type="text"
                                  className="min-w-0 flex-1 border rounded p-2 text-sm"
                                  value={customQueenRace}
                                  onChange={e => setCustomQueenRace(e.target.value)}
                                  placeholder="Seçili cinsin alt türü"
                              />
                              <button
                                  type="button"
                                  onClick={handleAddCustomQueenRace}
                                  className="shrink-0 rounded bg-purple-600 px-3 py-2 text-sm font-bold text-white hover:bg-purple-700"
                              >
                                  Ekle
                              </button>
                          </div>
                      </div>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Durumu</label>
                      <select 
                          className="w-full border rounded p-2"
                          value={editForm.queen_status || ''} onChange={e => setEditForm({...editForm, queen_status: e.target.value})}
                      >
                          <option value="">Seçiniz</option>
                          <option value="Verimli">Verimli</option>
                          <option value="Zayıf">Zayıf</option>
                          <option value="Meme Dikti">Meme Dikti</option>
                          <option value="Yok">Yok / Kesildi</option>
                      </select>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Notlar</label>
                      <textarea 
                          className="w-full border rounded p-2"
                          rows="3"
                          value={editForm.notes || ''} onChange={e => setEditForm({...editForm, notes: e.target.value})}
                      ></textarea>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                      <button type="submit" className="flex-1 bg-blue-500 text-white p-2 rounded">Kaydet</button>
                      <button type="button" onClick={deleteHive} className="bg-red-100 text-red-500 p-2 rounded px-4"><Trash2 size={20}/></button>
                  </div>
              </form>
          ) : (
              <div className="flex flex-col items-center text-center mt-2">
                <div className="bg-white p-2 rounded border mb-2">
                    <QRCode value={hive.qr_code || ''} size={80} />
                </div>
                <p className="text-xs text-gray-400 mb-2">{hive.qr_code}</p>
                <h2 className="text-2xl font-bold text-gray-800">{hive.name}</h2>
                <p className="text-gray-600 mb-1">{hive.apiary ? `${hive.apiary} - ` : ''}{hive.location}</p>

                {(() => {
                    const hm = getHealthMeta(hive.health_score, hive.health_status);
                    return (
                        <div className="w-full bg-gray-50 border rounded-lg p-3 mb-3 text-left">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-gray-700">Saglik Skoru</span>
                                <span className={`text-xs border px-2 py-0.5 rounded-full ${hm.className}`}>
                                    {hive.health_score ?? 100}/100 - {hm.label}
                                </span>
                            </div>
                            <div className="h-2 bg-white rounded-full overflow-hidden border">
                                <div className={`h-full ${hm.barClassName}`} style={{ width: `${Math.max(0, Math.min(100, Number(hive.health_score ?? 100)))}%` }}></div>
                            </div>
                            {hive.attention_reason && <p className="text-xs text-gray-500 mt-2">{hive.attention_reason}</p>}
                        </div>
                    );
                })()}
                
                {/* Hive Notes */}
                {hive.notes && (
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 text-sm text-gray-700 w-full mb-3 text-left">
                        <span className="font-bold block text-amber-800 mb-1">Notlar:</span>
                        {hive.notes}
                    </div>
                )}
                
                {/* Queen Info Badge */}
                {(hive.queen_year || hive.queen_race) && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full border border-purple-100 mb-4">
                        <span className="text-lg">👑</span>
                        <div className="text-left leading-tight">
                            <span className="block text-xs font-bold text-purple-700">
                                Ana Arı: {hive.queen_race || 'Belirsiz'}
                            </span>
                            {hive.queen_year && (
                                <span className={`text-[10px] px-1.5 rounded-full text-white ${getQueenColorClass(hive.queen_year)}`}>
                                    {hive.queen_year} Doğumlu
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <Link to={`/hive/${id}/log`} className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-full font-medium hover:bg-blue-600 w-full md:w-auto">
                    + Bakım Ekle
                </Link>
              </div>
          )}
        </div>

        {/* Notlardan Otomatik Özet */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="font-bold text-gray-800 mb-3">Özet / Değerlendirme</h3>
          {(() => {
            const now = new Date();
            const last30 = new Date();
            last30.setDate(now.getDate() - 30);
            const last30Logs = logs.filter(l => new Date(l.date) >= last30);
            
            const last30Counts = {
              feeding: last30Logs.filter(l => l.type === 'feeding').length,
              treatment: last30Logs.filter(l => l.type === 'treatment').length,
              harvest: last30Logs.filter(l => l.type === 'harvest').length,
            };
            
            const condCounts = {};
            logs.forEach(l => {
              if (l.condition) {
                condCounts[l.condition] = (condCounts[l.condition] || 0) + 1;
              }
            });
            
            const keywords = [
              { key: 'varroa', label: 'Varroa' },
              { key: 'oğul', label: 'Oğul' },
              { key: 'ana arı yok', label: 'Ana Arı Yok' },
              { key: 'hastalık', label: 'Hastalık' },
              { key: 'zayıf', label: 'Zayıf' },
              { key: 'güçlü', label: 'Güçlü' },
              { key: 'oksalik', label: 'Oksalik' },
              { key: 'formik', label: 'Formik' },
              { key: 'şurup', label: 'Şurup' },
              { key: 'kek', label: 'Kek' },
            ];
            const noteCounts = {};
            logs.forEach(l => {
              if (!l.notes) return;
              const n = l.notes.toLowerCase();
              keywords.forEach(k => {
                if (n.includes(k.key)) {
                  noteCounts[k.label] = (noteCounts[k.label] || 0) + 1;
                }
              });
            });
            const noteEntries = Object.entries(noteCounts).sort((a,b) => b[1]-a[1]).slice(0,8);
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border bg-amber-50/40">
                  <div className="text-xs text-gray-600 mb-1">Son 30 Gün</div>
                  <div className="flex gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-700">{last30Counts.feeding}</div>
                      <div className="text-[11px] text-gray-600">Besleme</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-700">{last30Counts.treatment}</div>
                      <div className="text-[11px] text-gray-600">İlaçlama</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-amber-700">{last30Counts.harvest}</div>
                      <div className="text-[11px] text-gray-600">Hasat</div>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-lg border bg-gray-50">
                  <div className="text-xs text-gray-600 mb-2">Genel Durum Dağılımı</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(condCounts).length === 0 && <span className="text-xs text-gray-400">Kayıt yok</span>}
                    {Object.entries(condCounts).map(([k,v]) => (
                      <span key={k} className="text-[11px] bg-white border rounded-full px-2 py-0.5">
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg border bg-gray-50">
                  <div className="text-xs text-gray-600 mb-2">Notlardan Anahtarlar</div>
                  <div className="flex flex-wrap gap-2">
                    {noteEntries.length === 0 && <span className="text-xs text-gray-400">Uygun anahtar bulunamadı</span>}
                    {noteEntries.map(([k,v]) => (
                      <span key={k} className="text-[11px] bg-white border rounded-full px-2 py-0.5">
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
            <button 
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'history' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500'}`}
            >
                Geçmiş
            </button>
            <button 
                onClick={() => setActiveTab('todos')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'todos' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500'}`}
            >
                Yapılacaklar
                {todos.filter(t => !t.completed).length > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{todos.filter(t => !t.completed).length}</span>
                )}
            </button>
            <button 
                onClick={() => setActiveTab('gallery')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'gallery' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500'}`}
            >
                Galeri
            </button>
        </div>

        {/* Tab Content */}
        <div>
            {activeTab === 'history' && (
                <div className="space-y-6">
                    {Object.entries(logGroups).map(([groupName, groupLogs]) => (
                        groupLogs.length > 0 && (
                            <div key={groupName} className="bg-white rounded-lg shadow overflow-hidden">
                                <div className="bg-amber-100 px-4 py-2 font-bold text-amber-800 flex items-center gap-2">
                                    {groupName === 'Hasat' && <Archive size={18} />}
                                    {groupName === 'Besleme' && <Droplet size={18} />}
                                    {groupName === 'İlaçlama' && <Pill size={18} />}
                                    {groupName === 'Kontrol' && <ClipboardList size={18} />}
                                    {groupName}
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {groupLogs.map(log => (
                                        <div key={log.id} className="p-4 hover:bg-gray-50 relative group">
                                            <button 
                                                onClick={() => deleteLog(log.id)}
                                                className="absolute top-2 right-2 text-gray-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition"
                                                title="Sil"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="font-semibold text-gray-800">
                                                    {log.sub_type ? log.sub_type : (log.condition || 'Genel')}
                                                    {log.quantity ? ` (${log.quantity} ${groupName === 'Hasat' ? 'kg' : 'lt/kg'})` : ''}
                                                </span>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                    {new Date(log.date).toLocaleDateString('tr-TR')}
                                                </span>
                                            </div>
                                            {log.notes && (
                                                <p className="text-sm text-gray-600 bg-amber-50/50 p-2 rounded mt-1 border border-amber-50/50">
                                                    {log.notes}
                                                </p>
                                            )}
                                            {log.photo_url && (
                                                <div className="mt-2 w-full relative">
                                                <button type="button" className="w-full relative" onClick={() => setLightboxSrc(log.photo_url)}>
                                                    <img src={log.photo_url} alt="Bakım görseli" className="w-full max-h-[70vh] rounded-lg object-contain border bg-gray-50 cursor-zoom-in" />
                                                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] px-2 py-1 rounded-full">
                                                      Büyüt
                                                    </div>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="absolute top-2 left-2 bg-white/90 hover:bg-white text-red-600 rounded-full p-2 shadow"
                                                  onClick={(e) => { e.stopPropagation(); deleteLogPhoto(log.id); }}
                                                  aria-label="Görseli sil"
                                                  title="Görseli sil"
                                                >
                                                  <Trash2 size={16} />
                                                </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                    
                    {logs.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                            <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                <ClipboardList size={32} className="text-gray-300" />
                            </div>
                            <p className="text-gray-500 font-medium">Henüz bakım kaydı bulunmuyor.</p>
                            <p className="text-xs text-gray-400 mt-1">Yukarıdaki "+ Bakım Ekle" butonunu kullanarak işlem yapabilirsiniz.</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'todos' && (
                <div className="space-y-4">
                    <form onSubmit={addTodo} className="flex gap-2">
                        <input 
                            type="text" 
                            className="flex-1 border rounded-lg p-2 shadow-sm"
                            placeholder="Yeni görev ekle..."
                            value={newTodo}
                            onChange={e => setNewTodo(e.target.value)}
                        />
                        <button type="submit" className="bg-green-500 text-white p-2 rounded-lg font-bold hover:bg-green-600">
                            <Plus size={20} />
                        </button>
                    </form>
                    
                    <div className="space-y-2">
                        {todos.map(todo => (
                            <div key={todo.id} className={`flex items-center justify-between p-3 rounded-lg border ${todo.completed ? 'bg-gray-50 border-gray-200 opacity-70' : 'bg-white border-blue-100 shadow-sm'}`}>
                                <div className="flex items-center gap-3" onClick={() => toggleTodo(todo.id)}>
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer ${todo.completed ? 'bg-green-500 border-green-500' : 'border-gray-400'}`}>
                                        {todo.completed && <span className="text-white text-xs">✓</span>}
                                    </div>
                                    <span className={`${todo.completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>{todo.text}</span>
                                </div>
                                <button onClick={() => deleteTodo(todo.id)} className="text-gray-300 hover:text-red-500">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                        {todos.length === 0 && <p className="text-center text-gray-400 py-4">Yapılacak iş yok.</p>}
                    </div>
                </div>
            )}

            {activeTab === 'gallery' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {galleryPhotos.map((photo, idx) => (
                        <button key={idx} type="button" className="relative aspect-square rounded-lg overflow-hidden shadow-sm bg-gray-100" onClick={() => setLightboxSrc(photo.url)}>
                            <img src={photo.url} alt="Galeri görseli" className="w-full h-full object-cover cursor-zoom-in" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1 text-center">
                                {new Date(photo.date).toLocaleDateString('tr-TR')}
                            </div>
                            <button
                              type="button"
                              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 rounded-full p-2 shadow"
                              onClick={(e) => { e.stopPropagation(); deleteLogPhoto(photo.id); }}
                              aria-label="Görseli sil"
                              title="Görseli sil"
                            >
                              <Trash2 size={16} />
                            </button>
                        </button>
                    ))}
                    {galleryPhotos.length === 0 && <p className="col-span-2 text-center text-gray-500 py-4">Henüz fotoğraf yok.</p>}
                </div>
            )}
        </div>

        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      </div>
    </Layout>
  );
}

function AddLog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('inspection'); // inspection, harvest, feeding, treatment
  
  // Common
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [photoLightboxSrc, setPhotoLightboxSrc] = useState(null);
  const previewUrlRef = React.useRef(null);
  const [notes, setNotes] = useState('');
  const [isListening, setIsListening] = useState(false);

  // Inspection
  const [condition, setCondition] = useState('Normal');
  const [inspectionMode, setInspectionMode] = useState('quick');
  const [queenSeen, setQueenSeen] = useState('Belirsiz');
  const [broodPattern, setBroodPattern] = useState('Belirsiz');
  const [eggsLarvae, setEggsLarvae] = useState('Belirsiz');
  const [colonyStrength, setColonyStrength] = useState('Orta');
  const [temperament, setTemperament] = useState('Sakin');
  const [foodStores, setFoodStores] = useState('Yeterli');
  const [varroaLevel, setVarroaLevel] = useState('Bakilmadi');
  
  // Harvest
  const [harvestType, setHarvestType] = useState('Bal');
  const [harvestAmount, setHarvestAmount] = useState('');

  // Feeding
  const [feedType, setFeedType] = useState('Şerbet');
  const [feedAmount, setFeedAmount] = useState('');

  // Treatment
  const [treatmentType, setTreatmentType] = useState('Varroa');
  
  const [submitting, setSubmitting] = useState(false);

  // --- Voice Recognition Setup ---
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      // Browser doesn't support speech recognition
      return;
    }
  }, []);

  const toggleListening = () => {
    if (isListening) {
      setIsListening(false);
      // Stop logic is handled by onend event usually, but we can force stop
      if (window.recognition) window.recognition.stop();
    } else {
      startListening();
    }
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Tarayıcınız sesli not özelliğini desteklemiyor.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'tr-TR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setNotes(prev => (prev ? prev + ' ' + transcript : transcript));
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    window.recognition = recognition;
    recognition.start();
  };
  // -------------------------------

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        try { URL.revokeObjectURL(previewUrlRef.current); } catch (e) { void e; }
      }
    };
  }, []);

  const setPreviewUrl = (url) => {
    if (previewUrlRef.current) {
      try { URL.revokeObjectURL(previewUrlRef.current); } catch (e) { void e; }
    }
    previewUrlRef.current = url;
    setPreview(url);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleCameraCapture = (blob) => {
    const filename = `photo_${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
    const file = new File([blob], filename, { type: 'image/jpeg' });
    setPhoto(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const detailLines = [];
    if (tab === 'inspection' && inspectionMode !== 'quick') {
        detailLines.push(`Kontrol modu: ${inspectionMode === 'detailed' ? 'Detayli' : 'Standart'}`);
        detailLines.push(`Ana ari goruldu: ${queenSeen}`);
        detailLines.push(`Koloni gucu: ${colonyStrength}`);
        detailLines.push(`Davranis: ${temperament}`);
        if (inspectionMode === 'detailed') {
            detailLines.push(`Yavru duzeni: ${broodPattern}`);
            detailLines.push(`Yumurta/larva: ${eggsLarvae}`);
            detailLines.push(`Besin stoku: ${foodStores}`);
            detailLines.push(`Varroa seviyesi: ${varroaLevel}`);
        }
    }
    const combinedNotes = [notes.trim(), detailLines.length ? `Detayli kontrol: ${detailLines.join(' | ')}` : '']
        .filter(Boolean)
        .join('\n');

    const formData = new FormData();
    formData.append('hive_id', id);
    formData.append('date', date);
    formData.append('type', tab);
    formData.append('notes', combinedNotes);
    if (photo) formData.append('photo', photo);

    if (tab === 'inspection') {
        formData.append('condition', condition);
    } else if (tab === 'harvest') {
        formData.append('sub_type', harvestType);
        formData.append('quantity', harvestAmount);
    } else if (tab === 'feeding') {
        formData.append('sub_type', feedType);
        formData.append('quantity', feedAmount);
    } else if (tab === 'treatment') {
        formData.append('sub_type', treatmentType);
    }

    try {
      await axios.post('/api/inspections', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      navigate(`/hive/${id}`);
    } catch (err) {
      alert('Hata: ' + err.message);
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Bakım / Veri Ekle">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        
        {/* Tabs */}
        <div className="flex border-b">
            <button type="button" onClick={() => setTab('inspection')} className={`flex-1 py-3 text-sm font-medium ${tab === 'inspection' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}>Kontrol</button>
            <button type="button" onClick={() => setTab('harvest')} className={`flex-1 py-3 text-sm font-medium ${tab === 'harvest' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}>Hasat</button>
            <button type="button" onClick={() => setTab('feeding')} className={`flex-1 py-3 text-sm font-medium ${tab === 'feeding' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}>Besleme</button>
            <button type="button" onClick={() => setTab('treatment')} className={`flex-1 py-3 text-sm font-medium ${tab === 'treatment' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}>İlaç</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Tarih</label>
            <input type="date" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          
          {tab !== 'inspection' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Genel Durum</label>
              <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={condition} onChange={e => setCondition(e.target.value)}>
                  <option>Normal</option>
                  <option>Zayıf</option>
                  <option>Güçlü</option>
                  <option>Oğul Verdi</option>
                  <option>Ana Arı Yok</option>
                  <option>Hastalık Var</option>
              </select>
            </div>
          )}

          {/* Dynamic Fields based on Tab */}
          {tab === 'inspection' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Durum</label>
                <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={condition} onChange={e => setCondition(e.target.value)}>
                    <option>Normal</option>
                    <option>Zayıf</option>
                    <option>Güçlü</option>
                    <option>Oğul Verdi</option>
                    <option>Ana Arı Yok</option>
                    <option>Hastalık Var</option>
                </select>
              </div>
          )}

          {tab === 'inspection' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kayit Modu</label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {[
                      ['quick', 'Hizli'],
                      ['standard', 'Standart'],
                      ['detailed', 'Detayli']
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setInspectionMode(value)}
                        className={`border rounded-lg py-2 text-sm font-bold ${inspectionMode === value ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {inspectionMode !== 'quick' && (
                  <div className="grid grid-cols-2 gap-3 bg-gray-50 border rounded-lg p-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Ana ari</label>
                      <select className="mt-1 w-full border rounded p-2 text-sm" value={queenSeen} onChange={e => setQueenSeen(e.target.value)}>
                        <option>Belirsiz</option>
                        <option>Goruldu</option>
                        <option>Gorulmedi</option>
                        <option>Meme var</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Koloni gucu</label>
                      <select className="mt-1 w-full border rounded p-2 text-sm" value={colonyStrength} onChange={e => setColonyStrength(e.target.value)}>
                        <option>Zayif</option>
                        <option>Orta</option>
                        <option>Guclu</option>
                        <option>Cok guclu</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Davranis</label>
                      <select className="mt-1 w-full border rounded p-2 text-sm" value={temperament} onChange={e => setTemperament(e.target.value)}>
                        <option>Sakin</option>
                        <option>Hareketli</option>
                        <option>Agresif</option>
                      </select>
                    </div>
                    {inspectionMode === 'detailed' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-600">Yavru duzeni</label>
                          <select className="mt-1 w-full border rounded p-2 text-sm" value={broodPattern} onChange={e => setBroodPattern(e.target.value)}>
                            <option>Belirsiz</option>
                            <option>Duzenli</option>
                            <option>Daginiik</option>
                            <option>Yok</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600">Yumurta/larva</label>
                          <select className="mt-1 w-full border rounded p-2 text-sm" value={eggsLarvae} onChange={e => setEggsLarvae(e.target.value)}>
                            <option>Belirsiz</option>
                            <option>Var</option>
                            <option>Az</option>
                            <option>Yok</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600">Besin stoku</label>
                          <select className="mt-1 w-full border rounded p-2 text-sm" value={foodStores} onChange={e => setFoodStores(e.target.value)}>
                            <option>Az</option>
                            <option>Yeterli</option>
                            <option>Iyi</option>
                            <option>Cok iyi</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600">Varroa</label>
                          <select className="mt-1 w-full border rounded p-2 text-sm" value={varroaLevel} onChange={e => setVarroaLevel(e.target.value)}>
                            <option>Bakilmadi</option>
                            <option>Dusuk</option>
                            <option>Orta</option>
                            <option>Yuksek</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
          )}

          {tab === 'harvest' && (
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ürün</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={harvestType} onChange={e => setHarvestType(e.target.value)}>
                        <option>Bal</option>
                        <option>Polen</option>
                        <option>Propolis</option>
                        <option>Arı Sütü</option>
                        <option>Balmumu</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Miktar (Kg)</label>
                    <input type="number" step="0.1" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={harvestAmount} onChange={e => setHarvestAmount(e.target.value)} placeholder="0.0" />
                  </div>
              </div>
          )}

          {tab === 'feeding' && (
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Besin</label>
                    <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={feedType} onChange={e => setFeedType(e.target.value)}>
                        <option>Şerbet</option>
                        <option>Kek</option>
                        <option>Fondant</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Miktar (Lt/Kg)</label>
                    <input type="number" step="0.1" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={feedAmount} onChange={e => setFeedAmount(e.target.value)} placeholder="0.0" />
                  </div>
              </div>
          )}

          {tab === 'treatment' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">İlaç / Tedavi Türü</label>
                <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" value={treatmentType} onChange={e => setTreatmentType(e.target.value)} placeholder="Örn: Oksalik Asit" />
              </div>
          )}

          <div>
            <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Notlar</label>
                <button 
                    type="button" 
                    onClick={toggleListening}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all shadow-sm ${
                        isListening 
                        ? 'bg-red-500 text-white animate-pulse ring-4 ring-red-200' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                    title="Sesle Yaz"
                    style={{ minHeight: '48px' }} // Eldivenle kolay basılması için minimum yükseklik
                >
                    {isListening ? <MicOff size={24} /> : <Mic size={24} />}
                    <span className="text-sm">{isListening ? 'DURDUR' : 'KONUŞ'}</span>
                </button>
            </div>
            <textarea 
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-3 text-lg ${isListening ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : ''}`}
                rows="4" 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                placeholder={isListening ? "Sizi dinliyorum..." : "Buraya yazın veya konuşmak için butona basın..."}
            ></textarea>
            {isListening && <p className="text-sm text-red-600 mt-2 font-medium flex items-center gap-2"><Activity className="animate-pulse" size={16}/> Konuşmanız metne çevriliyor...</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotoğraf</label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Camera className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Foto</p>
                </div>
                <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handlePhotoChange} />
              </label>

              <button type="button" onClick={() => setShowCamera(true)} className="flex flex-col items-center justify-center w-24 h-24 border-2 border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex flex-col items-center justify-center">
                  <Camera className="w-8 h-8 text-blue-500 mb-2" />
                  <p className="text-sm text-gray-600">Canlı</p>
                </div>
              </button>
              
              {preview && (
                <div className="relative w-24 h-24">
                    <button type="button" className="w-full h-full" onClick={() => setPhotoLightboxSrc(preview)}>
                      <img src={preview} alt="Preview" className="w-full h-full object-cover rounded-lg cursor-zoom-in" />
                      <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        Büyüt
                      </div>
                    </button>
                    <button type="button" onClick={() => {setPhoto(null); setPreviewUrl(null);}} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-sm">
                        <Trash2 size={12} />
                    </button>
                </div>
              )}
            </div>
          </div>

          <button disabled={submitting} type="submit" className="w-full bg-blue-600 text-white p-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
            <Save size={20} />
            {submitting ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </form>
      </div>

      <CameraCaptureModal open={showCamera} onClose={() => setShowCamera(false)} onCapture={handleCameraCapture} />
      <ImageLightbox src={photoLightboxSrc} onClose={() => setPhotoLightboxSrc(null)} />
    </Layout>
  );
}

function QRScan() {
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState(null);
  const [matchedHive, setMatchedHive] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Başlatılıyor...");
  const codeReaderRef = React.useRef(new BrowserMultiFormatReader());
  const isScanningRef = React.useRef(true);
  const videoRef = React.useRef(null);

  useEffect(() => {
    const codeReader = codeReaderRef.current;
    let selectedDeviceId;
    isScanningRef.current = true;

    codeReader.listVideoInputDevices()
      .then((videoInputDevices) => {
        if (videoInputDevices.length === 0) {
            setError("Kamera bulunamadı.");
            setStatus("Kamera bulunamadı");
            return;
        }

        // Arka kamerayı bulmaya çalış (genellikle sonuncusu veya 'back' içeren)
        const backCamera = videoInputDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('environment') ||
            device.label.toLowerCase().includes('arka')
        );
        
        selectedDeviceId = backCamera ? backCamera.deviceId : videoInputDevices[0].deviceId;
        
        setStatus(`Kamera açılıyor... (${backCamera ? 'Arka' : 'Varsayılan'})`);
        
        if (videoRef.current) {
            codeReader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (result, err) => {
              if (result && isScanningRef.current) {
                isScanningRef.current = false;
                codeReader.reset(); // Taramayı durdur
                setStatus("Kod okundu: " + result.getText());
                handleScan(result.getText());
              }
              if (err && !(err instanceof NotFoundException)) {
                // console.error(err)
              }
            }).catch(err => {
               if (err && err.name === 'NotAllowedError') {
                   setError("Kamera izni reddedildi.");
                   setStatus("İzin hatası");
               }
            });
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Kamera hatası: " + err.message);
        setStatus("Hata oluştu");
      });

    return () => {
      isScanningRef.current = false;
      codeReader.reset();
    };
  }, []);

  const handleScan = async (code) => {
    if (!code) return;
    
    try {
      // Find hive by QR code
      const res = await axios.get('/api/hives');
      const hive = res.data.data.find(h => h.qr_code === code);
      
      if (hive) {
        // Okuma başarılı, yönlendir
        setMatchedHive(hive);
        setScanResult(code);
        setError(null);
      } else {
        setScanResult('Bulunamadı: ' + code);
        setError('Bu koda ait kovan bulunamadı.');
        // İsteğe bağlı: 2 saniye sonra tekrar taramaya başla
        // setTimeout(() => { isScanningRef.current = true; codeReaderRef.current.decodeFromVideoDevice(...) }, 2000);
      }
    } catch (err) {
      console.error(err);
      setError("Veri çekilemedi.");
    }
  };

  const resetScan = () => {
    setMatchedHive(null);
    setScanResult(null);
    setError(null);
    window.location.reload();
  };

  return (
    <Layout title="QR Tara">
      <div className="bg-white p-4 rounded-lg shadow text-center">
        <h2 className="mb-4 font-bold text-gray-700">Kovan Barkodunu Okutun</h2>
        
        {matchedHive ? (
            <div className="text-left">
                <div className="bg-green-50 border border-green-100 text-green-800 p-4 rounded-lg mb-4">
                    <div className="text-xs font-bold uppercase tracking-wide">Kod okundu</div>
                    <div className="text-2xl font-bold mt-1">{matchedHive.name}</div>
                    <div className="text-sm opacity-80">{matchedHive.apiary || 'Arilik yok'} {matchedHive.location ? `- ${matchedHive.location}` : ''}</div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(`/hive/${matchedHive.id}/log`)}
                        className="w-full bg-blue-600 text-white rounded-lg p-4 font-bold flex items-center justify-center gap-2"
                    >
                        <Plus size={20} /> Bakim Ekle
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate(`/hive/${matchedHive.id}`)}
                        className="w-full bg-amber-500 text-white rounded-lg p-4 font-bold flex items-center justify-center gap-2"
                    >
                        <ClipboardList size={20} /> Kovan Detayi
                    </button>
                    <button
                        type="button"
                        onClick={resetScan}
                        className="w-full bg-gray-100 text-gray-700 rounded-lg p-3 font-bold"
                    >
                        Tekrar Tara
                    </button>
                </div>
            </div>
        ) : error ? (
            <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
                {error}
            </div>
        ) : (
            <div className="relative rounded-lg overflow-hidden bg-black aspect-square">
                <video ref={videoRef} className="w-full h-full object-cover"></video>
                <div className="absolute inset-0 border-4 border-amber-500/50 pointer-events-none flex items-center justify-center">
                    <div className="w-64 h-64 border-2 border-amber-400 rounded-lg"></div>
                </div>
                <div className="absolute bottom-2 left-0 w-full text-white text-xs bg-black/50 p-1">
                    {status}
                </div>
            </div>
        )}

        {scanResult && <p className="mt-4 text-red-500 font-bold">{scanResult}</p>}
        
        <p className="mt-4 text-sm text-gray-500">
          Kamera açılmazsa tarayıcı izinlerini kontrol edin.
        </p>
      </div>
    </Layout>
  );
}

function HandbookAdmin() {
    const emptyTopic = { id: null, slug: '', title: '', category: 'Genel', risk: 'Orta', summary: '', symptoms: '', checks: '', actions: '', source: '', is_active: 1 };
    const [topics, setTopics] = useState([]);
    const [stats, setStats] = useState([]);
    const [form, setForm] = useState(emptyTopic);
    const [message, setMessage] = useState('');

    const fetchData = async () => {
        const [topicsRes, statsRes] = await Promise.all([
            axios.get('/api/admin/handbook/topics'),
            axios.get('/api/admin/handbook/stats')
        ]);
        setTopics(topicsRes.data.data || []);
        setStats(statsRes.data.data || []);
    };

    useEffect(() => { fetchData().catch(() => {}); }, []);

    const editTopic = (topic) => {
        setForm({
            ...topic,
            symptoms: (topic.symptoms || []).join('\n'),
            checks: (topic.checks || []).join('\n'),
            actions: (topic.actions || []).join('\n')
        });
    };

    const submitTopic = async (e) => {
        e.preventDefault();
        const payload = { ...form };
        if (form.id) await axios.put(`/api/admin/handbook/topics/${form.id}`, payload);
        else await axios.post('/api/admin/handbook/topics', payload);
        setMessage('El kitabı konusu kaydedildi');
        setForm(emptyTopic);
        fetchData();
    };

    return (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="bg-white rounded-lg shadow p-5">
                <h2 className="font-bold text-lg mb-4">El Kitabı Konuları</h2>
                {message && <div className="bg-green-50 text-green-700 border border-green-100 rounded p-2 text-sm mb-3">{message}</div>}
                <form onSubmit={submitTopic} className="grid md:grid-cols-2 gap-3 mb-6">
                    <input className="border rounded p-2" placeholder="Başlık" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
                    <input className="border rounded p-2" placeholder="Slug" value={form.slug || ''} onChange={e => setForm({...form, slug: e.target.value})} />
                    <input className="border rounded p-2" placeholder="Kategori" value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
                    <select className="border rounded p-2" value={form.risk} onChange={e => setForm({...form, risk: e.target.value})}>
                        {['Kritik', 'Yüksek', 'Orta', 'Düşük'].map(item => <option key={item}>{item}</option>)}
                    </select>
                    <textarea className="border rounded p-2 md:col-span-2" rows="2" placeholder="Özet" value={form.summary} onChange={e => setForm({...form, summary: e.target.value})}></textarea>
                    <textarea className="border rounded p-2" rows="5" placeholder="Belirtiler - her satır ayrı" value={form.symptoms} onChange={e => setForm({...form, symptoms: e.target.value})}></textarea>
                    <textarea className="border rounded p-2" rows="5" placeholder="İlk kontrol - her satır ayrı" value={form.checks} onChange={e => setForm({...form, checks: e.target.value})}></textarea>
                    <textarea className="border rounded p-2" rows="5" placeholder="Aksiyonlar - her satır ayrı" value={form.actions} onChange={e => setForm({...form, actions: e.target.value})}></textarea>
                    <textarea className="border rounded p-2" rows="5" placeholder="Kaynak" value={form.source} onChange={e => setForm({...form, source: e.target.value})}></textarea>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Number(form.is_active) === 1} onChange={e => setForm({...form, is_active: e.target.checked ? 1 : 0})} /> Aktif</label>
                    <div className="flex gap-2">
                        <button className="bg-amber-500 text-white rounded px-4 py-2 font-bold">{form.id ? 'Güncelle' : 'Ekle'}</button>
                        <button type="button" onClick={() => setForm(emptyTopic)} className="border rounded px-4 py-2 font-bold">Temizle</button>
                    </div>
                </form>
                <div className="divide-y border rounded">
                    {topics.map(topic => (
                        <div key={topic.id} className="p-3 flex items-start justify-between gap-3">
                            <div>
                                <div className="font-bold">{topic.title} <span className="text-xs text-gray-400">/{topic.slug}</span></div>
                                <div className="text-xs text-gray-500">{topic.category} - {topic.risk} - {topic.is_active ? 'Aktif' : 'Pasif'}</div>
                            </div>
                            <button onClick={() => editTopic(topic)} className="text-blue-600 font-bold text-sm">Düzenle</button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-white rounded-lg shadow p-5">
                <h2 className="font-bold text-lg mb-4">El Kitabı Kullanımı</h2>
                <div className="space-y-2">
                    {stats.map((item, idx) => (
                        <div key={idx} className="border rounded p-2 text-sm">
                            <div className="font-bold">{item.query || item.topic_slug || item.event_type || '-'}</div>
                            <div className="text-xs text-gray-500">{item.event_type} - {item.count} kez</div>
                        </div>
                    ))}
                    {stats.length === 0 && <div className="text-sm text-gray-500">Henüz kullanım verisi yok.</div>}
                </div>
            </div>
        </div>
    );
}

function ChatModerationAdmin() {
    const [messages, setMessages] = useState([]);
    const fetchMessages = async () => {
        const res = await axios.get('/api/admin/chat/messages');
        setMessages(res.data.data || []);
    };
    useEffect(() => { fetchMessages().catch(() => {}); }, []);
    const setVisibility = async (msg) => {
        await axios.put(`/api/admin/chat/${msg.id}/visibility`, { is_hidden: msg.is_hidden ? 0 : 1 });
        fetchMessages();
    };
    return (
        <div className="bg-white rounded-lg shadow p-5">
            <h2 className="font-bold text-lg mb-4">Sohbet Moderasyonu</h2>
            <div className="divide-y border rounded">
                {messages.map(msg => (
                    <div key={msg.id} className={`p-3 flex items-start justify-between gap-3 ${msg.is_hidden ? 'bg-gray-50 opacity-70' : ''}`}>
                        <div>
                            <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500">
                                <b className="text-gray-800">{msg.username}</b>
                                <span>{msg.category}</span>
                                {msg.topic_title && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{msg.topic_title}</span>}
                                {msg.is_pinned ? <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Sabit</span> : null}
                            </div>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{msg.message || 'Fotoğraf mesajı'}</p>
                        </div>
                        <button onClick={() => setVisibility(msg)} className={`text-sm font-bold px-3 py-1 rounded ${msg.is_hidden ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {msg.is_hidden ? 'Göster' : 'Gizle'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AdminPanel() {
    const [activeTab, setActiveTab] = useState('users'); // users, announcements, support, stats
    const [users, setUsers] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState(null);
    const [backups, setBackups] = useState([]); // Backups state
    const [queenRaces, setQueenRaces] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null); // For admin view
    const [ticketMessages, setTicketMessages] = useState([]);
    const [adminReply, setAdminReply] = useState('');
    
    // User Forms
    const [newUser, setNewUser] = useState({ username: '', password: '' });
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ max_hives: 5, subscription_end: '', is_active: 1, password: '' });
    
    // Announcement Form
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', type: 'info' });
    const [newQueenRace, setNewQueenRace] = useState('');

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (activeTab === 'users') fetchUsers();
        if (activeTab === 'announcements') fetchAnnouncements();
        if (activeTab === 'support') fetchTickets();
        if (activeTab === 'stats') fetchStats();
        if (activeTab === 'backups') fetchBackups();
        if (activeTab === 'queenRaces') fetchQueenRaces();
    }, [activeTab]);

    const fetchUsers = async () => {
        try {
            const res = await axios.get('/api/admin/users');
            setUsers(res.data.data);
        } catch (err) {
            console.error(err);
            // If 403, it means not admin, maybe redirect or show error
            if (err.response && err.response.status === 403) {
                 // Silent fail or alert
                 console.warn("Admin yetkisi yok.");
            }
        }
    };

    const fetchAnnouncements = async () => {
        try {
            const res = await axios.get('/api/announcements'); 
            setAnnouncements(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchTickets = async () => {
        try {
            const res = await axios.get('/api/admin/support/tickets');
            setTickets(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchStats = async () => {
        try {
            const res = await axios.get('/api/admin/stats/global');
            setStats(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchBackups = async () => {
        try {
            const res = await axios.get('/api/admin/backups');
            setBackups(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchQueenRaces = async () => {
        try {
            const res = await axios.get('/api/admin/queen-races');
            setQueenRaces(res.data.data || []);
        } catch (err) { console.error(err); }
    };

    const handleAddQueenRace = async (e) => {
        e.preventDefault();
        const name = newQueenRace.trim();
        if (!name) return;
        try {
            await axios.post('/api/admin/queen-races', { name });
            setNewQueenRace('');
            setSuccess("Arı cinsi eklendi!");
            fetchQueenRaces();
        } catch (err) {
            setError(err.response?.data?.error || "Arı cinsi eklenemedi");
        }
    };

    const handleDeleteQueenRace = async (id) => {
        if(!confirm("Bu arı cinsi listeden kaldırılsın mı?")) return;
        try {
            await axios.delete(`/api/admin/queen-races/${id}`);
            fetchQueenRaces();
        } catch(err) { alert("Silinemedi"); }
    };

    const handleCreateBackup = async () => {
        if(!confirm("Yeni bir yedek oluşturulsun mu?")) return;
        try {
            await axios.post('/api/admin/backups');
            alert("Yedekleme başarılı!");
            fetchBackups();
        } catch (err) { alert("Yedekleme hatası"); }
    };

    const handleDownloadBackup = async (filename) => {
        // Token'ı URL'e ekle
        const token = localStorage.getItem('token');
        if (token) {
            window.open(`/api/admin/backups/${filename}?token=${token}`, '_blank');
        } else {
            alert("Oturum süresi dolmuş, lütfen tekrar giriş yapın.");
        }
    };

    const fetchTicketDetails = async (id) => {
        try {
            const res = await axios.get(`/api/support/tickets/${id}`);
            setActiveTicket(res.data.ticket);
            setTicketMessages(res.data.messages);
        } catch (err) { console.error(err); }
    };

    const handleAdminReply = async (e) => {
        e.preventDefault();
        if (!adminReply.trim()) return;
        try {
            await axios.post(`/api/support/tickets/${activeTicket.id}/messages`, { message: adminReply });
            setAdminReply('');
            fetchTicketDetails(activeTicket.id);
            fetchTickets(); // Refresh list to update status if needed
        } catch (err) { alert("Cevap gönderilemedi"); }
    };

    const handleCloseTicket = async (id) => {
        if(!confirm("Talep kapatılsın mı?")) return;
        try {
            await axios.put(`/api/admin/support/tickets/${id}/status`, { status: 'closed' });
            fetchTickets();
            if (activeTicket && activeTicket.id === id) fetchTicketDetails(id);
        } catch(err) { alert("İşlem başarısız"); }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError(null); setSuccess(null);
        try {
            await axios.post('/api/admin/users', newUser);
            setSuccess("Kullanıcı oluşturuldu!");
            setNewUser({ username: '', password: '' });
            fetchUsers();
        } catch (err) { setError(err.response?.data?.error); }
    };

    const handleAddAnnouncement = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/admin/announcements', newAnnouncement);
            setSuccess("Duyuru yayınlandı!");
            setNewAnnouncement({ title: '', content: '', type: 'info' });
            fetchAnnouncements();
        } catch (err) { setError("Duyuru eklenemedi"); }
    };

    const handleDeleteAnnouncement = async (id) => {
        if(!confirm("Silinsin mi?")) return;
        try {
            await axios.delete(`/api/admin/announcements/${id}`);
            fetchAnnouncements();
        } catch(err) { alert("Silinemedi"); }
    };

    // ... (handleDeleteUser, openEditModal, handleUpdateUser, getPackageLabel same as before) ...
    const handleDeleteUser = async (id) => {
        if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
        try {
            await axios.delete(`/api/admin/users/${id}`);
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.error || "Silme işlemi başarısız");
        }
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setEditForm({
            max_hives: user.max_hives || 5,
            subscription_end: user.subscription_end ? user.subscription_end.split('T')[0] : '',
            is_active: user.is_active,
            password: ''
        });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                max_hives: parseInt(editForm.max_hives),
                is_active: parseInt(editForm.is_active),
                subscription_end: editForm.subscription_end || null,
            };
            if (editForm.password) payload.password = editForm.password;

            await axios.put(`/api/admin/users/${editingUser.id}`, payload);
            setEditingUser(null);
            fetchUsers();
            alert("Kullanıcı güncellendi!");
        } catch (err) {
            alert("Güncelleme hatası: " + err.message);
        }
    };

    const getPackageLabel = (user) => {
        if (!user.subscription_end) return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">Süresiz</span>;
        const endDate = new Date(user.subscription_end);
        const daysLeft = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
        if (daysLeft < 0) return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Süresi Doldu</span>;
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">{daysLeft} Gün Kaldı</span>;
    };

    return (
        <Layout title="Admin Paneli">
            {/* Tabs */}
            <div className="flex border-b mb-6 bg-white rounded-t-lg overflow-x-auto">
                <button 
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'users' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <UserPlus className="inline mr-2" size={20}/> Kullanıcılar
                </button>
                <button 
                    onClick={() => setActiveTab('announcements')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'announcements' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <Bell className="inline mr-2" size={20}/> Duyurular
                </button>
                <button 
                    onClick={() => setActiveTab('support')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'support' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <MessageSquare className="inline mr-2" size={20}/> Destek
                </button>
                <button 
                    onClick={() => setActiveTab('stats')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'stats' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <BarChart3 className="inline mr-2" size={20}/> İstatistik
                </button>
                <button 
                    onClick={() => setActiveTab('backups')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'backups' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <Database className="inline mr-2" size={20}/> Yedekleme
                </button>
                <button 
                    onClick={() => setActiveTab('handbook')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'handbook' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <BookOpen className="inline mr-2" size={20}/> El Kitabı
                </button>
                <button
                    onClick={() => setActiveTab('queenRaces')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'queenRaces' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <Settings className="inline mr-2" size={20}/> Arı Cinsleri
                </button>
                <button 
                    onClick={() => setActiveTab('chatmod')}
                    className={`flex-1 min-w-[100px] py-4 font-bold text-center whitespace-nowrap ${activeTab === 'chatmod' ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-500' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    <MessageSquare className="inline mr-2" size={20}/> Sohbet Mod
                </button>
            </div>

            {error && <div className="bg-red-100 text-red-700 p-3 mb-4 rounded shadow-sm">{error}</div>}
            {success && <div className="bg-green-100 text-green-700 p-3 mb-4 rounded shadow-sm">{success}</div>}

            {activeTab === 'users' && (
                <>
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                        <h2 className="text-lg font-bold mb-4 text-gray-700">Hızlı Kullanıcı Ekle</h2>
                        <form onSubmit={handleCreateUser} className="flex gap-4 items-end">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500">Kullanıcı Adı</label>
                                <input
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-500">Şifre</label>
                                <input
                                    type="text"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                            <button type="submit" className="bg-amber-500 text-white px-6 py-2 rounded hover:bg-amber-600 h-[42px] font-bold">
                                Oluştur
                            </button>
                        </form>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md overflow-hidden">
                        <h2 className="text-lg font-bold mb-4 text-gray-700">Kullanıcı Listesi ({users.length})</h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kovan / Limit</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.map(u => (
                                        <tr key={u.id} className={u.is_active === 0 ? 'bg-red-50' : ''}>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{u.id}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                {u.username}
                                                {u.role === 'admin' && <span className="ml-2 bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5 rounded">Admin</span>}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min((u.hives_count / (u.max_hives || 5)) * 100, 100)}%` }}></div>
                                                    </div>
                                                    <span>{u.hives_count} / {u.max_hives || 5}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                <div className="flex flex-col gap-1">
                                                    {u.is_active === 1 
                                                        ? <span className="text-green-600 font-bold text-xs flex items-center gap-1"><CheckSquare size={12}/> Aktif</span> 
                                                        : <span className="text-red-600 font-bold text-xs flex items-center gap-1"><X size={12}/> Pasif</span>
                                                    }
                                                    {getPackageLabel(u)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {parseServerDate(u.created_at)?.toLocaleDateString('tr-TR')}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button onClick={() => openEditModal(u)} className="text-blue-600 hover:text-blue-900 mr-3" title="Düzenle">
                                                    <Edit size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteUser(u.id)} className="text-red-600 hover:text-red-900" title="Sil">
                                                    <Trash2 size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'handbook' && <HandbookAdmin />}
            {activeTab === 'chatmod' && <ChatModerationAdmin />}

            {activeTab === 'queenRaces' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-md md:col-span-1">
                        <h2 className="text-lg font-bold mb-4 text-gray-700">Yeni Arı Cinsi</h2>
                        <form onSubmit={handleAddQueenRace} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500">Cins / Irk Adı</label>
                                <input
                                    type="text"
                                    value={newQueenRace}
                                    onChange={(e) => setNewQueenRace(e.target.value)}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    placeholder="Örn: Trakya Ekotipi"
                                    required
                                />
                            </div>
                            <button type="submit" className="bg-amber-500 text-white px-6 py-2 rounded hover:bg-amber-600 font-bold w-full">
                                Ekle
                            </button>
                        </form>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
                        <h2 className="text-lg font-bold mb-4 text-gray-700">Arı Cinsi Listesi ({queenRaces.filter(r => r.is_active === 1).length})</h2>
                        <div className="space-y-2">
                            {queenRaces.map(race => (
                                <div key={race.id} className={`flex items-center justify-between border rounded p-3 ${race.is_active === 0 ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                                    <div>
                                        <div className="font-bold text-gray-800">{race.name}</div>
                                        {race.is_active === 0 && <div className="text-xs text-gray-500">Pasif</div>}
                                    </div>
                                    {race.is_active === 1 && (
                                        <button onClick={() => handleDeleteQueenRace(race.id)} className="text-red-500 hover:text-red-700" title="Listeden kaldır">
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {queenRaces.length === 0 && <p className="text-center text-gray-500 py-8">Henüz arı cinsi yok.</p>}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'announcements' && (
                <>
                    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                        <h2 className="text-lg font-bold mb-4 text-gray-700">Yeni Duyuru Yayınla</h2>
                        <form onSubmit={handleAddAnnouncement} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500">Başlık</label>
                                <input
                                    type="text"
                                    value={newAnnouncement.title}
                                    onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500">İçerik</label>
                                <textarea
                                    value={newAnnouncement.content}
                                    onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    rows="3"
                                    required
                                ></textarea>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500">Tür</label>
                                <select 
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    value={newAnnouncement.type}
                                    onChange={(e) => setNewAnnouncement({...newAnnouncement, type: e.target.value})}
                                >
                                    <option value="info">Bilgi (Mavi)</option>
                                    <option value="warning">Uyarı (Turuncu)</option>
                                    <option value="success">Başarı (Yeşil)</option>
                                    <option value="danger">Kritik (Kırmızı)</option>
                                </select>
                            </div>
                            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-bold w-full">
                                Yayınla
                            </button>
                        </form>
                    </div>

                    <div className="space-y-3">
                        {announcements.map(ann => (
                            <div key={ann.id} className="bg-white p-4 rounded-lg shadow flex justify-between items-start border-l-4 border-gray-300">
                                <div>
                                    <h3 className="font-bold text-gray-800">{ann.title}</h3>
                                    <p className="text-sm text-gray-600">{ann.content}</p>
                                    <span className="text-xs text-gray-400 mt-1 block">
                                        {parseServerDate(ann.created_at)?.toLocaleDateString('tr-TR')} - {ann.type}
                                    </span>
                                </div>
                                <button onClick={() => handleDeleteAnnouncement(ann.id)} className="text-red-400 hover:text-red-600">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                        {announcements.length === 0 && <p className="text-center text-gray-500">Aktif duyuru yok.</p>}
                    </div>
                </>
            )}

            {activeTab === 'support' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
                    {/* Ticket List */}
                    <div className="md:col-span-1 bg-white rounded-lg shadow-md overflow-y-auto">
                        <div className="p-4 border-b font-bold text-gray-700 sticky top-0 bg-white">Destek Talepleri</div>
                        {tickets.map(t => (
                            <div 
                                key={t.id} 
                                onClick={() => {
                                    fetchTicketDetails(t.id);
                                }}
                                className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${activeTicket?.id === t.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="font-bold text-sm text-gray-800 truncate">{t.subject}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        t.status === 'open' ? 'bg-green-100 text-green-800' :
                                        t.status === 'answered' ? 'bg-blue-100 text-blue-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>{t.status}</span>
                                </div>
                                <div className="text-xs text-gray-500 flex justify-between">
                                    <span>{t.username}</span>
                                    <span>{parseServerDate(t.created_at)?.toLocaleDateString('tr-TR')}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Ticket Detail & Reply */}
                    <div className="md:col-span-2 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
                        {activeTicket ? (
                            <>
                                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-bold">{activeTicket.subject}</h3>
                                        <p className="text-xs text-gray-500">Kullanıcı: {activeTicket.username || 'Bilinmiyor'} - Durum: {activeTicket.status}</p>
                                    </div>
                                    <button onClick={() => handleCloseTicket(activeTicket.id)} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200">
                                        Talebi Kapat
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                    {ticketMessages.map(msg => (
                                        <div key={msg.id} className={`flex flex-col ${msg.role === 'admin' ? 'items-end' : 'items-start'}`}>
                                            <div className={`max-w-[85%] rounded-lg p-3 text-sm ${
                                                msg.role === 'admin' ? 'bg-blue-600 text-white rounded-tr-none' : 
                                                'bg-gray-100 text-gray-800 rounded-tl-none'
                                            }`}>
                                                <div className="text-xs font-bold mb-1 opacity-75">{msg.username} ({msg.role})</div>
                                                <p className="whitespace-pre-wrap">{msg.message}</p>
                                                <div className={`text-[10px] mt-1 text-right ${msg.role === 'admin' ? 'text-blue-200' : 'text-gray-400'}`}>
                                                    {parseServerDate(msg.created_at)?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <form onSubmit={handleAdminReply} className="p-4 border-t bg-gray-50">
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            className="flex-1 border rounded p-2"
                                            placeholder="Cevap yazın..."
                                            value={adminReply}
                                            onChange={e => setAdminReply(e.target.value)}
                                        />
                                        <button type="submit" className="bg-blue-600 text-white px-4 rounded font-bold hover:bg-blue-700">Gönder</button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                                Bir talep seçin.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'stats' && stats && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                            <div className="text-3xl font-bold text-gray-800">{stats.totalUsers}</div>
                            <div className="text-sm text-gray-500">Toplam Kullanıcı</div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-amber-500">
                            <div className="text-3xl font-bold text-gray-800">{stats.totalHives}</div>
                            <div className="text-sm text-gray-500">Toplam Kovan</div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                            <div className="text-3xl font-bold text-gray-800">{parseFloat(stats.totalHoney).toFixed(1)} Kg</div>
                            <div className="text-sm text-gray-500">Toplam Bal Hasadı</div>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
                            <div className="text-3xl font-bold text-gray-800">{stats.openTickets}</div>
                            <div className="text-sm text-gray-500">Açık Destek Talebi</div>
                        </div>
                    </div>

                    {/* System Health */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-bold mb-4 text-gray-700 border-b pb-2">Sistem Sağlığı</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <div className="text-xs text-gray-500">DB Boyutu</div>
                                <div className="font-bold">{stats.system?.dbSizeMB} MB</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Çalışma Süresi</div>
                                <div className="font-bold">{(stats.system?.uptimeSeconds / 3600).toFixed(1)} Saat</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Node Sürümü</div>
                                <div className="font-bold">{stats.system?.nodeVersion}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Platform</div>
                                <div className="font-bold uppercase">{stats.system?.platform}</div>
                            </div>
                        </div>
                    </div>

                    {/* Activity Logs */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-bold mb-4 text-gray-700 border-b pb-2">Son Aktiviteler</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Tarih</th>
                                        <th className="px-4 py-2 text-left">Kullanıcı</th>
                                        <th className="px-4 py-2 text-left">İşlem</th>
                                        <th className="px-4 py-2 text-left">Detay</th>
                                        <th className="px-4 py-2 text-left">IP</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {stats.activities?.map((log, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                                                            {parseServerDate(log.created_at)?.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                                            </td>
                                            <td className="px-4 py-2 font-bold text-gray-700">
                                                {log.username || 'Sistem/Misafir'}
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                    log.action === 'LOGIN' ? 'bg-green-100 text-green-800' :
                                                    log.action === 'ADD_HIVE' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>{log.action}</span>
                                            </td>
                                            <td className="px-4 py-2 text-gray-600 truncate max-w-xs" title={log.details}>
                                                {log.details}
                                            </td>
                                            <td className="px-4 py-2 text-gray-500 text-xs">
                                                {(log.ip_address || '').replace(/^::ffff:/, '')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {(!stats.activities || stats.activities.length === 0) && (
                                <p className="text-center py-4 text-gray-500">Henüz kayıt yok.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'backups' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Yedekleme Merkezi</h2>
                            <p className="text-xs text-gray-500">Veritabanını manuel olarak yedekleyin veya indirin.</p>
                        </div>
                        <button onClick={handleCreateBackup} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 flex items-center gap-2">
                            <Save size={20}/> Şimdi Yedekle
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left">Dosya Adı</th>
                                    <th className="px-4 py-2 text-left">Boyut</th>
                                    <th className="px-4 py-2 text-left">Oluşturulma Tarihi</th>
                                    <th className="px-4 py-2 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {backups.map((bk, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 font-mono text-gray-700">{bk.name}</td>
                                        <td className="px-4 py-3 text-gray-600">{bk.size}</td>
                                        <td className="px-4 py-3 text-gray-600">{parseServerDate(bk.created_at)?.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => handleDownloadBackup(bk.name)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 ml-auto">
                                                <Download size={16}/> İndir
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {backups.length === 0 && (
                            <p className="text-center py-10 text-gray-500">Henüz yedek bulunmuyor.</p>
                        )}
                    </div>
                </div>
            )}

            {/* Edit Modal (Same as before) */}
            {editingUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                            <h3 className="text-lg font-bold">Kullanıcı Düzenle: {editingUser.username}</h3>
                            <button onClick={() => setEditingUser(null)} className="text-gray-500 hover:text-gray-700"><X size={24}/></button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Kovan Limiti</label>
                                <input 
                                    type="number" className="w-full border rounded p-2"
                                    value={editForm.max_hives} onChange={e => setEditForm({...editForm, max_hives: e.target.value})}
                                />
                                <p className="text-xs text-gray-500">Hobi: 5, Profesyonel: 50, Ticari: 500+</p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Abonelik Bitiş Tarihi</label>
                                <input 
                                    type="date" className="w-full border rounded p-2"
                                    value={editForm.subscription_end} onChange={e => setEditForm({...editForm, subscription_end: e.target.value})}
                                />
                                <p className="text-xs text-gray-500">Boş bırakılırsa "Süresiz" olur.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Hesap Durumu</label>
                                <select 
                                    className="w-full border rounded p-2"
                                    value={editForm.is_active} onChange={e => setEditForm({...editForm, is_active: e.target.value})}
                                >
                                    <option value="1">Aktif (Giriş Yapabilir)</option>
                                    <option value="0">Pasif / Dondurulmuş (Giriş Yapamaz)</option>
                                </select>
                            </div>

                            <div className="border-t pt-4 mt-2">
                                <label className="block text-sm font-medium text-gray-700">Şifre Sıfırla (İsteğe Bağlı)</label>
                                <input 
                                    type="text" className="w-full border rounded p-2 bg-gray-50"
                                    placeholder="Değiştirmek için yeni şifre yazın..."
                                    value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})}
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200">İptal</button>
                                <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700">Değişiklikleri Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}


function CommunityChat() {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [category, setCategory] = useState('Genel');
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [topicTitle, setTopicTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const previewUrlRef = React.useRef(null);
    const chatCategories = ['Genel', 'Hastalik', 'Ana Ari', 'Besleme', 'Hasat', 'Ilan'];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchMessages = async () => {
        try {
            const res = await axios.get('/api/chat');
            setMessages(res.data.data || []);
            setPinnedMessages(res.data.pinned || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
        return () => {
            clearInterval(interval);
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        };
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        previewUrlRef.current = previewUrl;
        setPhoto(file);
        setPhotoPreview(previewUrl);
    };

    const clearPhoto = () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
        setPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && !photo) return;
        
        try {
            const formData = new FormData();
            formData.append('message', newMessage);
            formData.append('category', category);
            if (replyTo) formData.append('parent_id', replyTo.id);
            if (topicTitle.trim()) formData.append('topic_title', topicTitle.trim());
            if (photo) formData.append('photo', photo);
            await axios.post('/api/chat', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setNewMessage('');
            setReplyTo(null);
            setTopicTitle('');
            clearPhoto();
            fetchMessages();
        } catch (err) {
            alert("Mesaj gönderilemedi");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Silinsin mi?")) return;
        try {
            await axios.delete(`/api/admin/chat/${id}`);
            fetchMessages();
        } catch (err) {
            alert("Silinemedi");
        }
    };

    const handlePin = async (msg) => {
        try {
            await axios.put(`/api/admin/chat/${msg.id}/pin`, { is_pinned: msg.is_pinned ? 0 : 1 });
            fetchMessages();
        } catch (err) {
            alert("Sabit mesaj guncellenemedi");
        }
    };

    return (
        <Layout title="Arıcılar Kahvesi">
            <div className="flex flex-col h-[calc(100vh-140px)] bg-white rounded-lg shadow-md overflow-hidden">
                {/* Chat Header */}
                <div className="p-4 bg-amber-100 border-b flex justify-between items-center">
                    <div>
                        <h2 className="font-bold text-amber-800">Sohbet Odası</h2>
                        <p className="text-xs text-amber-600">Diğer arıcılarla bilgi paylaşın</p>
                    </div>
                    <div className="text-xs text-gray-500">
                        {messages.length} Mesaj
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {pinnedMessages.length > 0 && (
                        <div className="space-y-2">
                            {pinnedMessages.map(msg => (
                                <div key={`pin-${msg.id}`} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                    <div className="mb-1 flex items-center gap-2 font-semibold">
                                        <Bell size={14} />
                                        <span>Sabit duyuru</span>
                                        <span className="text-xs font-normal text-amber-700">{msg.username}</span>
                                    </div>
                                    <p className="break-words">{msg.message || 'Fotograf paylasildi'}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900">
                        Hastalık fotoğrafı veya belirti paylaşıyorsanız kategori seçin. Sistem benzer başlıklar için El Kitabı'na bakmanızı önerir.
                        <Link to="/handbook" className="font-bold ml-1 underline">El Kitabı</Link>
                    </div>
                    {loading && <p className="text-center text-gray-400">Yükleniyor...</p>}
                    {messages.map(msg => {
                        const isMe = msg.username === user.username;
                        const isAdminMessage = msg.role === 'admin' || msg.username === 'admin';
                        return (
                            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[86%] rounded-lg p-3 shadow-sm relative group ${isMe ? 'bg-amber-500 text-white rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'}`}>
                                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                                        {!isMe && <span className={`text-xs font-bold ${isAdminMessage ? 'text-blue-700' : 'text-amber-600'}`}>{msg.username}</span>}
                                        {isAdminMessage && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Yetkili</span>}
                                        {msg.category && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isMe ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>{msg.category}</span>}
                                        {msg.is_pinned ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isMe ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'}`}>Sabit</span> : null}
                                        {msg.topic_title ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isMe ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'}`}>{msg.topic_title}</span> : null}
                                    </div>
                                    {msg.parent_message && (
                                        <div className={`mb-2 rounded border-l-4 p-2 text-xs ${isMe ? 'bg-white/10 border-white/40 text-white' : 'bg-gray-50 border-amber-300 text-gray-600'}`}>
                                            <div className="font-bold">{msg.parent_username} mesajına yanıt</div>
                                            <div className="line-clamp-2">{msg.parent_message}</div>
                                        </div>
                                    )}
                                    {msg.message && <p className="text-sm break-words whitespace-pre-wrap">{msg.message}</p>}
                                    {msg.photo_url && (
                                        <button type="button" className="mt-2 block overflow-hidden rounded-lg" onClick={() => setLightboxSrc(msg.photo_url)}>
                                            <img src={msg.photo_url} alt="Sohbet fotografi" className="max-h-56 w-full object-cover" />
                                        </button>
                                    )}
                                    <div className={`text-[10px] mt-1 text-right ${isMe ? 'text-amber-100' : 'text-gray-400'}`}>
                                        {parseServerDate(msg.created_at)?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                    <button type="button" onClick={() => setReplyTo(msg)} className={`mt-2 text-[11px] font-bold ${isMe ? 'text-white' : 'text-amber-700'}`}>
                                        Yanıtla
                                    </button>
                                    
                                    {/* Admin Delete Button */}
                                    {user.username === 'admin' && (
                                        <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                            <button
                                                onClick={() => handlePin(msg)}
                                                className="bg-amber-600 text-white rounded-full p-1 shadow-sm"
                                                title={msg.is_pinned ? 'Sabitten kaldir' : 'Sabitle'}
                                            >
                                                <Bell size={10} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(msg.id)}
                                                className="bg-red-500 text-white rounded-full p-1 shadow-sm"
                                                title="Sil"
                                            >
                                                <X size={10} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <form onSubmit={handleSend} className="border-t bg-white p-3">
                    {replyTo && (
                        <div className="mb-2 flex items-start justify-between gap-2 rounded-lg bg-amber-50 border border-amber-100 p-2 text-xs text-amber-900">
                            <div>
                                <b>{replyTo.username}</b> mesajına yanıt: <span className="line-clamp-1">{replyTo.message || 'Fotoğraf'}</span>
                            </div>
                            <button type="button" onClick={() => setReplyTo(null)}><X size={14} /></button>
                        </div>
                    )}
                    <input
                        type="text"
                        className="mb-2 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Konu başlığı (isteğe bağlı)"
                        value={topicTitle}
                        onChange={e => setTopicTitle(e.target.value)}
                    />
                    {photoPreview && (
                        <div className="mb-2 flex items-center gap-2">
                            <button type="button" onClick={() => setLightboxSrc(photoPreview)} className="h-14 w-14 overflow-hidden rounded-lg border">
                                <img src={photoPreview} alt="Secilen fotograf" className="h-full w-full object-cover" />
                            </button>
                            <button type="button" onClick={clearPhoto} className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200" title="Fotografi kaldir">
                                <X size={16} />
                            </button>
                        </div>
                    )}
                    <div className="mb-2 flex gap-2 overflow-x-auto">
                        {chatCategories.map(item => (
                            <button key={item} type="button" onClick={() => setCategory(item)} className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${category === item ? 'border-amber-500 bg-amber-100 text-amber-800' : 'border-gray-200 bg-white text-gray-600'}`}>
                                {item}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-full border border-gray-200 p-2 text-gray-600 hover:bg-gray-50" title="Fotograf ekle">
                        <Camera size={20} />
                    </button>
                    <input ref={fileInputRef} type="file" className="hidden" accept="image/*" capture="environment" onChange={handlePhotoChange} />
                    <input 
                        type="text" 
                        className="min-w-0 flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Bir şeyler yazın..."
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                    />
                    <button type="submit" className="bg-amber-500 text-white p-2 rounded-full hover:bg-amber-600 shadow-sm transition disabled:opacity-50" disabled={!newMessage.trim() && !photo}>
                        <Send size={20} className="ml-0.5" />
                    </button>
                    </div>
                </form>
            </div>
            <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
        </Layout>
    );
}

function BeekeeperHandbook() {
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('Tümü');
    const [risk, setRisk] = useState('Tümü');
    const [activeTopicId, setActiveTopicId] = useState(HANDBOOK_TOPICS[0]?.id || '');
    const normalizedQuery = query.trim().toLocaleLowerCase('tr-TR');

    const filteredTopics = React.useMemo(() => {
        return HANDBOOK_TOPICS.filter(topic => {
            const text = [
                topic.title,
                topic.category,
                topic.risk,
                topic.summary,
                topic.symptoms.join(' '),
                topic.checks.join(' '),
                topic.actions.join(' ')
            ].join(' ').toLocaleLowerCase('tr-TR');
            const matchesQuery = !normalizedQuery || text.includes(normalizedQuery);
            const matchesCategory = category === 'Tümü' || topic.category === category;
            const matchesRisk = risk === 'Tümü' || topic.risk === risk;
            return matchesQuery && matchesCategory && matchesRisk;
        });
    }, [normalizedQuery, category, risk]);

    useEffect(() => {
        if (!filteredTopics.some(topic => topic.id === activeTopicId)) {
            setActiveTopicId(filteredTopics[0]?.id || '');
        }
    }, [filteredTopics, activeTopicId]);

    const activeTopic = filteredTopics.find(topic => topic.id === activeTopicId) || filteredTopics[0] || null;

    return (
        <Layout>
            <div className="space-y-5">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shadow p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-sm font-bold text-amber-100">
                                <BookOpen size={18} />
                                Arıcının El Kitabı
                            </div>
                            <h1 className="text-2xl md:text-3xl font-black mt-2">Sorun, hastalık ve bakım rehberi</h1>
                            <p className="text-sm md:text-base text-amber-50 mt-2 max-w-3xl">
                                Belirtiye, hastalığa veya sezona göre arama yapın. İçerikler saha kontrolü ve kayıt düzeni için hazırlanmıştır; ilaç/doz kararı için resmi talimat ve yetkili uzman görüşü gerekir.
                            </p>
                        </div>
                        <Link to="/" className="bg-white text-amber-700 px-4 py-2 rounded-lg font-bold text-sm hover:bg-amber-50 inline-flex items-center justify-center gap-2">
                            Kovan seç <ArrowLeft size={16} className="rotate-180" />
                        </Link>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Varroa, kanatsız arı, ana arı yok, besleme, yavru çürüklüğü..."
                            className="w-full border rounded-lg pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded-lg px-3 py-2 bg-white">
                            {HANDBOOK_CATEGORIES.map(item => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <select value={risk} onChange={e => setRisk(e.target.value)} className="border rounded-lg px-3 py-2 bg-white">
                            {HANDBOOK_RISKS.map(item => <option key={item} value={item}>{item === 'Tümü' ? 'Tüm riskler' : item}</option>)}
                        </select>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[360px_1fr] gap-5">
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-3 border-b flex items-center justify-between">
                            <span className="font-bold text-gray-700">Başlıklar</span>
                            <span className="text-xs text-gray-500">{filteredTopics.length} konu</span>
                        </div>
                        <div className="max-h-[62vh] overflow-y-auto divide-y">
                            {filteredTopics.map(topic => (
                                <button
                                    key={topic.id}
                                    type="button"
                                    onClick={() => setActiveTopicId(topic.id)}
                                    className={`w-full text-left p-4 hover:bg-amber-50 transition ${activeTopic?.id === topic.id ? 'bg-amber-50' : ''}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-bold text-gray-900">{topic.title}</div>
                                        <span className={`shrink-0 text-[10px] border px-2 py-0.5 rounded-full font-bold ${getRiskClass(topic.risk)}`}>{topic.risk}</span>
                                    </div>
                                    <div className="text-xs text-amber-700 font-semibold mt-1">{topic.category}</div>
                                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">{topic.summary}</p>
                                </button>
                            ))}
                            {filteredTopics.length === 0 && (
                                <div className="p-6 text-center text-sm text-gray-500">
                                    Aramanıza uygun konu bulunamadı.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-5">
                        {activeTopic ? (
                            <div className="space-y-5">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 border-b pb-4">
                                    <div>
                                        <div className="text-sm font-bold text-amber-700">{activeTopic.category}</div>
                                        <h2 className="text-2xl font-black text-gray-900 mt-1">{activeTopic.title}</h2>
                                        <p className="text-gray-600 mt-2">{activeTopic.summary}</p>
                                    </div>
                                    <span className={`w-fit text-xs border px-3 py-1 rounded-full font-bold ${getRiskClass(activeTopic.risk)}`}>{activeTopic.risk} risk</span>
                                </div>

                                <div className="grid md:grid-cols-3 gap-4">
                                    <div className="border rounded-lg p-4 bg-red-50/40">
                                        <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Belirtiler</h3>
                                        <ul className="space-y-2 text-sm text-gray-700">
                                            {activeTopic.symptoms.map(item => <li key={item}>• {item}</li>)}
                                        </ul>
                                    </div>
                                    <div className="border rounded-lg p-4 bg-blue-50/40">
                                        <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><CheckSquare size={16} /> İlk kontrol</h3>
                                        <ul className="space-y-2 text-sm text-gray-700">
                                            {activeTopic.checks.map(item => <li key={item}>• {item}</li>)}
                                        </ul>
                                    </div>
                                    <div className="border rounded-lg p-4 bg-green-50/40">
                                        <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><ClipboardList size={16} /> Önerilen aksiyon</h3>
                                        <ul className="space-y-2 text-sm text-gray-700">
                                            {activeTopic.actions.map(item => <li key={item}>• {item}</li>)}
                                        </ul>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    <div className="font-bold mb-1">Güvenli kullanım notu</div>
                                    Bu bölüm teşhis veya ilaç reçetesi değildir. Hastalık şüphesinde yetkili veteriner/arıcılık uzmanı ve güncel resmi talimatlar esas alınmalıdır. Bakım kaydı oluştururken gözlem, tarih, fotoğraf ve uygulama bilgisini net yazın.
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Link to="/" className="bg-amber-500 text-white px-4 py-3 rounded-lg font-bold text-center hover:bg-amber-600">
                                        Kovan seçip bakım kaydı oluştur
                                    </Link>
                                    <Link to="/chat" className="border border-gray-200 px-4 py-3 rounded-lg font-bold text-center hover:bg-gray-50">
                                        Sohbette konu aç
                                    </Link>
                                </div>

                                <div className="border-t pt-4 text-xs text-gray-500 flex items-start gap-2">
                                    <ExternalLink size={14} className="mt-0.5 shrink-0" />
                                    <span>Kaynak dayanağı: {activeTopic.source}. İçerikler kısa saha rehberi olarak özetlenmiştir.</span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 py-12">Konu seçin veya arama filtresini temizleyin.</div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function BeekeeperHandbookV2() {
    const [topics, setTopics] = useState(HANDBOOK_TOPICS.map(topic => ({ ...topic, id: topic.id || topic.slug })));
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('Tumu');
    const [risk, setRisk] = useState('Tumu');
    const [activeTopicId, setActiveTopicId] = useState(HANDBOOK_TOPICS[0]?.id || '');
    const [favorites, setFavorites] = useState(() => {
        try { return JSON.parse(localStorage.getItem('handbook_favorites') || '[]'); } catch (e) { return []; }
    });
    const normalizedQuery = query.trim().toLocaleLowerCase('tr-TR');
    const categories = React.useMemo(() => ['Tumu', ...Array.from(new Set(topics.map(topic => topic.category).filter(Boolean)))], [topics]);

    useEffect(() => {
        axios.get('/api/handbook/topics')
            .then(res => {
                const apiTopics = (res.data.data || []).map(topic => ({ ...topic, id: topic.slug || topic.id }));
                if (apiTopics.length) setTopics(apiTopics);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!normalizedQuery) return;
        const timer = setTimeout(() => {
            axios.post('/api/handbook/events', { event_type: 'search', query: query.trim() }).catch(() => {});
        }, 800);
        return () => clearTimeout(timer);
    }, [normalizedQuery, query]);

    const filteredTopics = React.useMemo(() => {
        return topics.filter(topic => {
            const text = [topic.title, topic.category, topic.risk, topic.summary, ...(topic.symptoms || []), ...(topic.checks || []), ...(topic.actions || [])].join(' ').toLocaleLowerCase('tr-TR');
            return (!normalizedQuery || text.includes(normalizedQuery)) &&
                (category === 'Tumu' || topic.category === category) &&
                (risk === 'Tumu' || topic.risk === risk);
        });
    }, [topics, normalizedQuery, category, risk]);

    useEffect(() => {
        if (!filteredTopics.some(topic => topic.id === activeTopicId)) setActiveTopicId(filteredTopics[0]?.id || '');
    }, [filteredTopics, activeTopicId]);

    const activeTopic = filteredTopics.find(topic => topic.id === activeTopicId) || filteredTopics[0] || null;
    const toggleFavorite = (topic) => {
        const id = topic.slug || topic.id;
        const next = favorites.includes(id) ? favorites.filter(item => item !== id) : [...favorites, id];
        setFavorites(next);
        localStorage.setItem('handbook_favorites', JSON.stringify(next));
        axios.post('/api/handbook/events', { event_type: 'favorite', topic_slug: id }).catch(() => {});
    };

    return (
        <Layout>
            <div className="space-y-5">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shadow p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-sm font-bold text-amber-100"><BookOpen size={18} /> Arıcının El Kitabı</div>
                            <h1 className="text-2xl md:text-3xl font-black mt-2">Sorun, hastalık ve bakım rehberi</h1>
                            <p className="text-sm md:text-base text-amber-50 mt-2 max-w-3xl">Belirtiye, hastalığa veya sezona göre arama yapın. Bu rehber teşhis ya da ilaç reçetesi yerine geçmez; resmi talimat ve yetkili uzman görüşü esas alınmalıdır.</p>
                        </div>
                        <Link to="/" className="bg-white text-amber-700 px-4 py-2 rounded-lg font-bold text-sm hover:bg-amber-50 inline-flex items-center justify-center gap-2">Kovan seç <ArrowLeft size={16} className="rotate-180" /></Link>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Varroa, kanatsız arı, ana arı yok, besleme, yavru çürüklüğü..." className="w-full border rounded-lg pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded-lg px-3 py-2 bg-white">
                            {categories.map(item => <option key={item} value={item}>{item === 'Tumu' ? 'Tüm kategoriler' : item}</option>)}
                        </select>
                        <select value={risk} onChange={e => setRisk(e.target.value)} className="border rounded-lg px-3 py-2 bg-white">
                            {['Tumu', 'Kritik', 'Yüksek', 'Orta', 'Düşük'].map(item => <option key={item} value={item}>{item === 'Tumu' ? 'Tüm riskler' : item}</option>)}
                        </select>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[360px_1fr] gap-5">
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-3 border-b flex items-center justify-between">
                            <span className="font-bold text-gray-700">Başlıklar</span>
                            <span className="text-xs text-gray-500">{filteredTopics.length} konu</span>
                        </div>
                        <div className="max-h-[62vh] overflow-y-auto divide-y">
                            {filteredTopics.map(topic => (
                                <button key={topic.id} type="button" onClick={() => setActiveTopicId(topic.id)} className={`w-full text-left p-4 hover:bg-amber-50 transition ${activeTopic?.id === topic.id ? 'bg-amber-50' : ''}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-bold text-gray-900">{topic.title}</div>
                                        <div className="flex items-center gap-1">
                                            {favorites.includes(topic.slug || topic.id) && <span className="text-amber-500 text-xs">★</span>}
                                            <span className={`shrink-0 text-[10px] border px-2 py-0.5 rounded-full font-bold ${getRiskClass(topic.risk)}`}>{topic.risk}</span>
                                        </div>
                                    </div>
                                    <div className="text-xs text-amber-700 font-semibold mt-1">{topic.category}</div>
                                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">{topic.summary}</p>
                                </button>
                            ))}
                            {filteredTopics.length === 0 && <div className="p-6 text-center text-sm text-gray-500">Aramanıza uygun konu bulunamadı.</div>}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-5">
                        {activeTopic ? (
                            <div className="space-y-5">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 border-b pb-4">
                                    <div>
                                        <div className="text-sm font-bold text-amber-700">{activeTopic.category}</div>
                                        <h2 className="text-2xl font-black text-gray-900 mt-1">{activeTopic.title}</h2>
                                        <p className="text-gray-600 mt-2">{activeTopic.summary}</p>
                                    </div>
                                    <div className="flex flex-col gap-2 items-start md:items-end">
                                        <span className={`w-fit text-xs border px-3 py-1 rounded-full font-bold ${getRiskClass(activeTopic.risk)}`}>{activeTopic.risk} risk</span>
                                        <button type="button" onClick={() => toggleFavorite(activeTopic)} className="text-xs font-bold text-amber-700 border rounded-full px-3 py-1">{favorites.includes(activeTopic.slug || activeTopic.id) ? 'Favoriden çıkar' : 'Favoriye ekle'}</button>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-3 gap-4">
                                    {[
                                        ['Belirtiler', activeTopic.symptoms || [], AlertTriangle, 'bg-red-50/40'],
                                        ['İlk kontrol', activeTopic.checks || [], CheckSquare, 'bg-blue-50/40'],
                                        ['Önerilen aksiyon', activeTopic.actions || [], ClipboardList, 'bg-green-50/40']
                                    ].map(([title, items, Icon, className]) => (
                                        <div key={title} className={`border rounded-lg p-4 ${className}`}>
                                            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><Icon size={16} /> {title}</h3>
                                            <ul className="space-y-2 text-sm text-gray-700">{items.map(item => <li key={item}>• {item}</li>)}</ul>
                                        </div>
                                    ))}
                                </div>

                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    <div className="font-bold mb-1">Güvenli kullanım notu</div>
                                    Bu bölüm teşhis veya ilaç reçetesi değildir. Hastalık şüphesinde yetkili veteriner/arıcılık uzmanı ve güncel resmi talimatlar esas alınmalıdır.
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Link to="/" className="bg-amber-500 text-white px-4 py-3 rounded-lg font-bold text-center hover:bg-amber-600">Kovan seçip bakım kaydı oluştur</Link>
                                    <Link to="/chat" className="border border-gray-200 px-4 py-3 rounded-lg font-bold text-center hover:bg-gray-50">Sohbette konu aç</Link>
                                </div>

                                <div className="border-t pt-4 text-xs text-gray-500 flex items-start gap-2">
                                    <ExternalLink size={14} className="mt-0.5 shrink-0" />
                                    <span>Kaynak dayanağı: {activeTopic.source}. İçerikler kısa saha rehberi olarak özetlenmiştir.</span>
                                </div>
                            </div>
                        ) : <div className="text-center text-gray-500 py-12">Konu seçin veya arama filtresini temizleyin.</div>}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function ToolsHub() {
    const today = new Date().toISOString().slice(0, 10);
    const [hives, setHives] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [finance, setFinance] = useState({ data: [], summary: { income: 0, expense: 0, profit: 0 } });
    const [analytics, setAnalytics] = useState(null);
    const [settings, setSettings] = useState({});
    const [activeTool, setActiveTool] = useState('offline');
    const [offlineDrafts, setOfflineDrafts] = useState(() => {
        try { return JSON.parse(localStorage.getItem('offline_inspections') || '[]'); } catch (e) { return []; }
    });
    const [draft, setDraft] = useState({ hive_id: '', notes: '', condition: 'Normal', date: today });
    const [reminderForm, setReminderForm] = useState({ hive_id: '', title: '', due_date: today, type: 'Bakım' });
    const [equipmentForm, setEquipmentForm] = useState({ hive_id: '', name: '', type: 'Kovan gövdesi', status: 'Aktif', cost: '', notes: '' });
    const [financeForm, setFinanceForm] = useState({ hive_id: '', type: 'expense', category: 'Şeker', amount: '', date: today, description: '' });
    const [parseText, setParseText] = useState('');
    const [parseResult, setParseResult] = useState(null);

    const refreshTools = async () => {
        const [ctx, rem, eq, fin, ana] = await Promise.all([
            axios.get('/api/tools/context'),
            axios.get('/api/tools/reminders'),
            axios.get('/api/tools/equipment'),
            axios.get('/api/tools/finance'),
            axios.get('/api/tools/analytics')
        ]);
        setHives(ctx.data.hives || []);
        setSettings(ctx.data.settings || {});
        setReminders(rem.data.data || []);
        setEquipment(eq.data.data || []);
        setFinance(fin.data || { data: [], summary: { income: 0, expense: 0, profit: 0 } });
        setAnalytics(ana.data.data || null);
    };

    useEffect(() => { refreshTools().catch(console.error); }, []);

    const saveDraft = () => {
        if (!draft.hive_id || !draft.notes.trim()) return alert('Kovan ve not gerekli');
        const next = [...offlineDrafts, { ...draft, id: Date.now(), saved_at: new Date().toISOString() }];
        setOfflineDrafts(next);
        localStorage.setItem('offline_inspections', JSON.stringify(next));
        setDraft({ hive_id: '', notes: '', condition: 'Normal', date: today });
    };

    const syncDrafts = async () => {
        const remaining = [];
        for (const item of offlineDrafts) {
            try {
                const form = new FormData();
                form.append('hive_id', item.hive_id);
                form.append('date', item.date);
                form.append('condition', item.condition);
                form.append('notes', item.notes);
                form.append('type', 'inspection');
                await axios.post('/api/inspections', form);
            } catch (e) {
                remaining.push(item);
            }
        }
        setOfflineDrafts(remaining);
        localStorage.setItem('offline_inspections', JSON.stringify(remaining));
        refreshTools();
        alert(remaining.length ? `${remaining.length} kayıt beklemede kaldı` : 'Tüm offline kayıtlar senkronize edildi');
    };

    const parseNote = async () => {
        if (!parseText.trim()) return;
        const res = await axios.post('/api/tools/parse-note', { text: parseText });
        setParseResult(res.data.data);
        setDraft(prev => ({ ...prev, notes: parseText, condition: res.data.data.condition || prev.condition }));
    };

    const addReminder = async (e) => {
        e.preventDefault();
        await axios.post('/api/tools/reminders', reminderForm);
        setReminderForm({ hive_id: '', title: '', due_date: today, type: 'Bakım' });
        refreshTools();
    };

    const addEquipment = async (e) => {
        e.preventDefault();
        await axios.post('/api/tools/equipment', equipmentForm);
        setEquipmentForm({ hive_id: '', name: '', type: 'Kovan gövdesi', status: 'Aktif', cost: '', notes: '' });
        refreshTools();
    };

    const addFinance = async (e) => {
        e.preventDefault();
        await axios.post('/api/tools/finance', financeForm);
        setFinanceForm({ hive_id: '', type: 'expense', category: 'Şeker', amount: '', date: today, description: '' });
        refreshTools();
    };

    const finishOnboarding = async () => {
        await axios.put('/api/tools/settings', { ...settings, onboarding_completed: 1 });
        refreshTools();
    };

    const hiveOptions = <>{hives.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</>;
    const money = (value) => `${Number(value || 0).toLocaleString('tr-TR')} TL`;
    const toolTabs = [
        ['offline', WifiOff, 'Offline'],
        ['reminders', CalendarClock, 'Hatırlatıcı'],
        ['ai', Bot, 'AI Not'],
        ['finance', Wallet, 'Finans'],
        ['equipment', Package, 'Ekipman'],
        ['analytics', BarChart3, 'Analiz'],
        ['map', Map, 'Harita/NFC'],
        ['onboarding', CheckSquare, 'Kurulum'],
        ['mentor', User, 'Uzman'],
        ['help', AlertTriangle, 'Yardım']
    ];

    return (
        <Layout>
            <div className="space-y-5">
                <div className="bg-white rounded-lg shadow p-5">
                    <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Wrench /> Arıcılık Araçları</h1>
                    <p className="text-sm text-gray-600 mt-2">Offline kayıt, hatırlatıcı, AI not ayrıştırma, finans, ekipman, analiz, harita/NFC, kurulum ve uzman akışları tek merkezde.</p>
                </div>

                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow p-5 text-white">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-xl font-black flex items-center gap-2"><Bot size={20} /> AI Petek Analizi</h2>
                            <p className="text-sm text-amber-50 mt-2 max-w-2xl">Fotoğraf yükleme, kovan bazlı analiz geçmişi ve gelecekte Python CV servislerine bağlanacak AI entegrasyon katmanı artık hazır.</p>
                        </div>
                        <Link to="/ai-analysis" className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-3 text-sm font-black text-amber-700 hover:bg-amber-50">
                            Analiz ekranını aç
                        </Link>
                    </div>
                </div>

                <div className="flex gap-2 overflow-x-auto pb-1">
                    {toolTabs.map(([key, Icon, label]) => (
                        <button key={key} onClick={() => setActiveTool(key)} className={`shrink-0 border rounded-lg px-3 py-2 text-xs font-bold flex items-center gap-1 ${activeTool === key ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-gray-600'}`}>
                            <Icon size={16} /> {label}
                        </button>
                    ))}
                </div>

                {activeTool === 'offline' && (
                    <div className="grid lg:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg shadow p-5 space-y-3">
                            <h2 className="font-black text-gray-900">Offline saha kaydı</h2>
                            <select value={draft.hive_id} onChange={e => setDraft({ ...draft, hive_id: e.target.value })} className="w-full border rounded-lg p-3"><option value="">Kovan seç</option>{hiveOptions}</select>
                            <div className="grid grid-cols-2 gap-2">
                                <input type="date" value={draft.date} onChange={e => setDraft({ ...draft, date: e.target.value })} className="border rounded-lg p-3" />
                                <select value={draft.condition} onChange={e => setDraft({ ...draft, condition: e.target.value })} className="border rounded-lg p-3"><option>Normal</option><option>Güçlü</option><option>Zayıf</option><option>Kritik</option></select>
                            </div>
                            <textarea value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} className="w-full border rounded-lg p-3" rows="4" placeholder="Sahada internet yoksa notu buraya yazın." />
                            <button onClick={saveDraft} className="w-full bg-amber-500 text-white rounded-lg py-3 font-bold">Cihaza kaydet</button>
                        </div>
                        <div className="bg-white rounded-lg shadow p-5">
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <h2 className="font-black text-gray-900">Bekleyen kayıtlar ({offlineDrafts.length})</h2>
                                <button onClick={syncDrafts} disabled={!offlineDrafts.length} className="bg-green-600 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg text-sm font-bold">Senkronize et</button>
                            </div>
                            <div className="space-y-2 max-h-80 overflow-y-auto">{offlineDrafts.map(item => <div key={item.id} className="border rounded-lg p-3 text-sm"><b>{hives.find(h => String(h.id) === String(item.hive_id))?.name || 'Kovan'}</b><p className="text-gray-600">{item.notes}</p></div>)}</div>
                        </div>
                    </div>
                )}

                {activeTool === 'reminders' && (
                    <div className="grid lg:grid-cols-2 gap-4">
                        <form onSubmit={addReminder} className="bg-white rounded-lg shadow p-5 space-y-3">
                            <h2 className="font-black text-gray-900">Hatırlatıcı ekle</h2>
                            <input required value={reminderForm.title} onChange={e => setReminderForm({ ...reminderForm, title: e.target.value })} className="w-full border rounded-lg p-3" placeholder="Örn: Varroa kontrolü" />
                            <div className="grid grid-cols-2 gap-2"><input type="date" value={reminderForm.due_date} onChange={e => setReminderForm({ ...reminderForm, due_date: e.target.value })} className="border rounded-lg p-3" /><select value={reminderForm.hive_id} onChange={e => setReminderForm({ ...reminderForm, hive_id: e.target.value })} className="border rounded-lg p-3"><option value="">Genel</option>{hiveOptions}</select></div>
                            <button className="w-full bg-amber-500 text-white rounded-lg py-3 font-bold">Kaydet</button>
                        </form>
                        <div className="bg-white rounded-lg shadow p-5 space-y-2">
                            <h2 className="font-black text-gray-900">Açık işler</h2>
                            {reminders.map(r => <button key={r.id} onClick={() => axios.put(`/api/tools/reminders/${r.id}/toggle`, { completed: r.completed ? 0 : 1 }).then(refreshTools)} className="w-full text-left border rounded-lg p-3"><b className={r.completed ? 'line-through text-gray-400' : ''}>{r.title}</b><div className="text-xs text-gray-500">{r.due_date || 'Tarihsiz'} {r.hive_name ? `- ${r.hive_name}` : ''}</div></button>)}
                        </div>
                    </div>
                )}

                {activeTool === 'ai' && (
                    <div className="bg-white rounded-lg shadow p-5 space-y-3">
                        <h2 className="font-black text-gray-900">AI destekli not ayrıştırma</h2>
                        <textarea value={parseText} onChange={e => setParseText(e.target.value)} className="w-full border rounded-lg p-3" rows="5" placeholder="Örn: 12 numara zayıf, 1 kg şurup verdim, varroa kontrolü gerekli." />
                        <button onClick={parseNote} className="bg-amber-500 text-white px-4 py-3 rounded-lg font-bold">Notu ayrıştır</button>
                        {parseResult && <div className="grid sm:grid-cols-4 gap-2 text-sm">{Object.entries(parseResult).map(([k, v]) => <div key={k} className="border rounded-lg p-3"><b>{k}</b><div>{String(v || '-')}</div></div>)}</div>}
                    </div>
                )}

                {activeTool === 'finance' && (
                    <div className="grid lg:grid-cols-2 gap-4">
                        <form onSubmit={addFinance} className="bg-white rounded-lg shadow p-5 space-y-3">
                            <h2 className="font-black text-gray-900">Gelir/gider ekle</h2>
                            <div className="grid grid-cols-2 gap-2"><select value={financeForm.type} onChange={e => setFinanceForm({ ...financeForm, type: e.target.value })} className="border rounded-lg p-3"><option value="expense">Gider</option><option value="income">Gelir</option></select><input required type="number" step="0.01" value={financeForm.amount} onChange={e => setFinanceForm({ ...financeForm, amount: e.target.value })} className="border rounded-lg p-3" placeholder="Tutar" /></div>
                            <input value={financeForm.category} onChange={e => setFinanceForm({ ...financeForm, category: e.target.value })} className="w-full border rounded-lg p-3" placeholder="Kategori" />
                            <input value={financeForm.description} onChange={e => setFinanceForm({ ...financeForm, description: e.target.value })} className="w-full border rounded-lg p-3" placeholder="Açıklama" />
                            <button className="w-full bg-amber-500 text-white rounded-lg py-3 font-bold">Kaydet</button>
                        </form>
                        <div className="bg-white rounded-lg shadow p-5">
                            <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm"><div className="border rounded-lg p-3"><b>Gelir</b><div>{money(finance.summary?.income)}</div></div><div className="border rounded-lg p-3"><b>Gider</b><div>{money(finance.summary?.expense)}</div></div><div className="border rounded-lg p-3"><b>Net</b><div>{money(finance.summary?.profit)}</div></div></div>
                            <div className="space-y-2 max-h-80 overflow-y-auto">{(finance.data || []).map(f => <div key={f.id} className="border rounded-lg p-3 text-sm flex justify-between"><span>{f.category || f.description}</span><b>{money(f.amount)}</b></div>)}</div>
                        </div>
                    </div>
                )}

                {activeTool === 'equipment' && (
                    <div className="grid lg:grid-cols-2 gap-4">
                        <form onSubmit={addEquipment} className="bg-white rounded-lg shadow p-5 space-y-3">
                            <h2 className="font-black text-gray-900">Ekipman ekle</h2>
                            <input required value={equipmentForm.name} onChange={e => setEquipmentForm({ ...equipmentForm, name: e.target.value })} className="w-full border rounded-lg p-3" placeholder="Ekipman adı" />
                            <div className="grid grid-cols-2 gap-2"><input value={equipmentForm.type} onChange={e => setEquipmentForm({ ...equipmentForm, type: e.target.value })} className="border rounded-lg p-3" placeholder="Tür" /><select value={equipmentForm.hive_id} onChange={e => setEquipmentForm({ ...equipmentForm, hive_id: e.target.value })} className="border rounded-lg p-3"><option value="">Genel</option>{hiveOptions}</select></div>
                            <button className="w-full bg-amber-500 text-white rounded-lg py-3 font-bold">Kaydet</button>
                        </form>
                        <div className="bg-white rounded-lg shadow p-5 space-y-2">{equipment.map(eq => <div key={eq.id} className="border rounded-lg p-3 text-sm"><b>{eq.name}</b><div className="text-gray-500">{eq.type} {eq.hive_name ? `- ${eq.hive_name}` : ''}</div></div>)}</div>
                    </div>
                )}

                {activeTool === 'analytics' && (
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg shadow p-5"><b>Kovan</b><div className="text-3xl font-black mt-2">{analytics?.hives?.total_hives || 0}</div><p className="text-sm text-gray-500">Ortalama sağlık: {Math.round(analytics?.hives?.avg_health || 0)}</p></div>
                        <div className="bg-white rounded-lg shadow p-5"><b>Hasat</b><div className="text-3xl font-black mt-2">{analytics?.harvest?.total_harvest || 0} kg</div><p className="text-sm text-gray-500">Kayıt: {analytics?.harvest?.harvest_records || 0}</p></div>
                        <div className="bg-white rounded-lg shadow p-5"><b>Arılık</b><div className="text-3xl font-black mt-2">{analytics?.apiaries?.length || 0}</div><p className="text-sm text-gray-500">Bölgesel takip için temel özet</p></div>
                    </div>
                )}

                {activeTool === 'map' && (
                    <div className="bg-white rounded-lg shadow p-5 space-y-3">
                        <h2 className="font-black text-gray-900">Harita ve NFC hazırlığı</h2>
                        {hives.map(h => <div key={h.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"><div><b>{h.name}</b><div className="text-sm text-gray-500">{h.apiary || h.location || 'Konum girilmemiş'}</div></div><div className="flex gap-2"><Link to={`/hive/${h.id}`} className="border rounded-lg px-3 py-2 text-sm font-bold">Kovan kartı</Link><a className="border rounded-lg px-3 py-2 text-sm font-bold" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.location || h.apiary || h.name)}`}>Harita</a></div></div>)}
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">NFC etikete yazılacak bağlantı formatı: https://kovan.3bbilgisayar.com/hive/KOVAN_ID</div>
                    </div>
                )}

                {activeTool === 'onboarding' && (
                    <div className="bg-white rounded-lg shadow p-5 space-y-4">
                        <h2 className="font-black text-gray-900">Kurulum kontrol listesi</h2>
                        {['Kovanları ekle', 'QR/NFC etiketlerini yerleştir', 'İlk bakım kayıtlarını gir', 'Hatırlatıcıları planla', 'El kitabı favorilerini seç'].map((item, index) => <div key={item} className="flex items-center gap-3 border rounded-lg p-3"><CheckSquare className="text-green-600" /><span>{index + 1}. {item}</span></div>)}
                        <button onClick={finishOnboarding} className="bg-green-600 text-white rounded-lg px-4 py-3 font-bold">{settings.onboarding_completed ? 'Kurulum tamamlandı' : 'Kurulumu tamamla'}</button>
                    </div>
                )}

                {activeTool === 'mentor' && <div className="bg-white rounded-lg shadow p-5"><h2 className="font-black text-gray-900 mb-2">Uzman/mentor akışı</h2><p className="text-gray-600 mb-4">Soru, fotoğraf ve konu başlığıyla topluluk sohbetinde destek isteyin. Kritik hastalık şüphesinde resmi uzman görüşü esas alınmalıdır.</p><Link to="/chat" className="bg-amber-500 text-white px-4 py-3 rounded-lg font-bold">Sohbete git</Link></div>}
                {activeTool === 'help' && <div className="bg-white rounded-lg shadow p-5"><h2 className="font-black text-gray-900 mb-2">Destek merkezi</h2><p className="text-gray-600 mb-4">Kullanım soruları, destek talepleri ve sorun bildirimleri için destek merkezini kullanın.</p><Link to="/help" className="bg-amber-500 text-white px-4 py-3 rounded-lg font-bold">Destek merkezini aç</Link></div>}
            </div>
        </Layout>
    );
}

function UpdatedHelpGuide() {
    const quickLinks = [
        { to: '/', icon: Home, title: 'Ana panel', text: 'Kovan durumu, dikkat listesi, hava ve hızlı işlemler.' },
        { to: '/new-hive', icon: Plus, title: 'Kovan ekle', text: 'Yeni kovan oluşturup QR kartını hazırlayın.' },
        { to: '/scan', icon: QrCode, title: 'QR tara', text: 'Sahada kovanı hızlı açın ve bakım kaydına geçin.' },
        { to: '/tools', icon: Wrench, title: 'Araçlar', text: 'Offline kayıt, hatırlatıcı, finans, ekipman ve analiz.' },
        { to: '/handbook', icon: BookOpen, title: 'El kitabı', text: 'Hastalık, zararlı ve sezon bakım rehberinde arama yapın.' },
        { to: '/chat', icon: MessageSquare, title: 'Sohbet', text: 'Fotoğrafla soru sorun, konu başlığı açın veya yanıtlayın.' }
    ];

    const guideSections = [
        {
            color: 'border-amber-500',
            icon: CheckSquare,
            title: 'İlk kurulum sırası',
            items: [
                'Ana panelden veya alt menüden yeni kovan ekleyin.',
                'Kovan adı, arılık ve ana arı bilgilerini mümkün olduğunca dolu girin.',
                'QR kodu yazdırıp kovana yerleştirin; NFC kullanacaksanız kovan detay bağlantısını etikete yazın.',
                'Araçlar > Kurulum bölümündeki kontrol listesini tamamlayın.'
            ]
        },
        {
            color: 'border-green-500',
            icon: ClipboardList,
            title: 'Bakım, hasat ve saha kaydı',
            items: [
                'Kovan detayındaki bakım ekleme ekranından kontrol, besleme, ilaçlama ve hasat kayıtlarını girin.',
                'Fotoğraf ve not ekleyerek aynı kovanın geçmişini düzenli tutun.',
                'İnternet zayıfsa Araçlar > Offline bölümüne taslak kaydedin; bağlantı gelince senkronize edin.',
                'Sesli not destekleyen tarayıcılarda notu konuşarak yazdırabilirsiniz.'
            ]
        },
        {
            color: 'border-blue-500',
            icon: CalendarClock,
            title: 'Hatırlatıcı, finans ve ekipman',
            items: [
                'Araçlar > Hatırlatıcı bölümünden varroa kontrolü, besleme veya hasat hazırlığı için tarihli iş açın.',
                'Finans bölümünde gelir-gider kaydı tutarak net durumu izleyin.',
                'Ekipman bölümünde kovan gövdesi, kat, yemlik ve benzeri malzemeleri kovanla ilişkilendirin.',
                'Analiz bölümünde kovan sayısı, ortalama sağlık, hasat ve arılık özetlerini kontrol edin.'
            ]
        },
        {
            color: 'border-lime-600',
            icon: BookOpen,
            title: 'El kitabı ve hastalık araştırması',
            items: [
                'Varroa, yavru çürüklüğü, ana arı kaybı ve sezon bakımı gibi konularda arama yapın.',
                'Sık kullandığınız başlıkları favoriye alın.',
                'El kitabı pratik saha rehberidir; hastalık ve ilaç kararında resmi uzman görüşünü esas alın.',
                'Bir konu hakkında emin değilseniz sohbetten fotoğraf ve konu başlığıyla destek isteyin.'
            ]
        },
        {
            color: 'border-purple-500',
            icon: Bot,
            title: 'AI not ayrıştırma',
            items: [
                'Araçlar > AI Not bölümüne saha notunuzu doğal dille yazın.',
                'Sistem notu işlem türü, durum, miktar ve alt başlık olarak ayrıştırır.',
                'Ayrıştırılan metni bakım taslağına aktarabilir, son kontrolü yaptıktan sonra kaydedebilirsiniz.',
                'AI sonucu öneri niteliğindedir; kayıt öncesi bilgileri mutlaka kontrol edin.'
            ]
        },
        {
            color: 'border-red-500',
            icon: AlertTriangle,
            title: 'Sık karşılaşılan sorunlar',
            items: [
                'QR kamera açılmıyorsa tarayıcı kamera iznini ve HTTPS bağlantısını kontrol edin.',
                'CSV Türkçe karakter sorunu yaşarsanız XLSX indirmeyi tercih edin veya CSV dosyasını UTF-8 olarak açın.',
                'Fotoğraf yüklenmiyorsa dosyanın resim formatında ve 8 MB altında olduğundan emin olun.',
                'Offline taslak senkron olmuyorsa bağlantıyı kontrol edip tekrar deneyin; başarısız kayıt cihazda kalır.'
            ]
        }
    ];

    const fullGuideSections = [
        ...guideSections,
        {
            color: 'border-emerald-500',
            icon: Download,
            title: 'Raporlar ve Türkçe karakter',
            items: [
                'Ana panelden tüm kovanlar için XLSX veya CSV raporu indirebilirsiniz.',
                'Tek kovanın detay sayfasından o kovana ait geçmiş kayıtlar CSV olarak indirilebilir.',
                'Excel Türkçe karakterleri CSV içinde bozuk gösterirse XLSX raporunu tercih edin.',
                'CSV kullanmanız gerekiyorsa dosyayı UTF-8 kodlamasıyla içe aktarın.'
            ]
        },
        {
            color: 'border-sky-500',
            icon: Camera,
            title: 'Mobil kullanım, kamera ve fotoğraf',
            items: [
                'Mobilde QR tarama, bakım kaydı, fotoğraf, sesli not, sohbet, el kitabı ve araçlar kullanılabilir.',
                'QR ve fotoğraf için kamera izni, sesli not için mikrofon izni gerekir.',
                'Fotoğraf yüklenmiyorsa dosyanın resim formatında ve 8 MB altında olduğunu kontrol edin.',
                'Kamera açılmazsa telefon ayarlarından tarayıcı izinlerini ve HTTPS bağlantısını kontrol edin.'
            ]
        },
        {
            color: 'border-indigo-500',
            icon: Settings,
            title: 'Profil, abonelik ve veri düzeni',
            items: [
                'Profil ikonundan şehir bilgisini güncelleyebilir ve şifrenizi değiştirebilirsiniz.',
                'Ana panelde abonelik bitiş tarihi, kovan hakkı ve kullanılan kovan sayısı görünür.',
                'Her kovanı benzersiz adla kaydedin; arılık/bölge alanı ana panelde gruplama sağlar.',
                'Kovan silme, bakım kaydı silme ve görsel silme işlemlerinde sistem onay ister.'
            ]
        },
        {
            color: 'border-gray-500',
            icon: User,
            title: 'Admin ve yönetim işlemleri',
            items: [
                'Admin kullanıcılar üst menüden admin paneline girebilir.',
                'Kullanıcılar, duyurular, destek talepleri, sistem istatistikleri ve yedekler yönetilebilir.',
                'El Kitabı başlıkları admin panelinden eklenebilir, düzenlenebilir ve aktif/pasif yapılabilir.',
                'Sohbet moderasyonunda mesaj gizleme, gösterme, silme ve sabitleme işlemleri yapılabilir.'
            ]
        }
    ];

    const workflowSteps = [
        'Giriş yapın ve profilinizde şehir bilgisini kontrol edin.',
        'Kovanları ekleyin, QR/NFC etiketlerini hazırlayın.',
        'İlk kontrol, fotoğraf ve not kayıtlarını oluşturun.',
        'Hatırlatıcı, finans ve ekipman kayıtlarını başlatın.',
        'El Kitabı ve Sohbet bölümlerini destek akışı olarak kullanın.'
    ];

    return (
        <div className="space-y-5 animate-fade-in">
            <div className="bg-white rounded-lg shadow-sm p-5">
                <h2 className="text-xl font-black text-gray-900">Yardım merkezi</h2>
                <p className="text-sm text-gray-600 mt-2">KovanTakip kullanıcı rehberindeki ana akışlar bu ekranda özetlenir. Kovan kaydı, QR, bakım, rapor, sohbet, el kitabı, araçlar, mobil kullanım ve destek talebi adımlarına buradan ulaşabilirsiniz.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                    {quickLinks.map(({ to, icon: Icon, title, text }) => (
                        <Link key={to} to={to} className="border rounded-lg p-3 hover:bg-amber-50 hover:border-amber-300 transition">
                            <div className="flex items-center gap-2 font-bold text-gray-900"><Icon size={18} className="text-amber-600" /> {title}</div>
                            <p className="text-xs text-gray-500 mt-1 leading-5">{text}</p>
                        </Link>
                    ))}
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {fullGuideSections.map(({ color, icon: Icon, title, items }) => (
                    <div key={title} className={`bg-white p-5 rounded-lg shadow-sm border-l-4 ${color}`}>
                        <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2"><Icon size={20} /> {title}</h3>
                        <ul className="space-y-2 text-sm text-gray-600">
                            {items.map(item => <li key={item} className="flex gap-2"><span className="text-amber-600 font-black">•</span><span>{item}</span></li>)}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2"><ClipboardList size={20} /> Önerilen kullanım sırası</h3>
                <div className="grid md:grid-cols-5 gap-2">
                    {workflowSteps.map((step, index) => (
                        <div key={step} className="border rounded-lg p-3 text-sm bg-gray-50">
                            <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center font-black mb-2">{index + 1}</div>
                            <p className="text-gray-700 leading-5">{step}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                <div className="font-black mb-1">Destek talebi ne zaman açılmalı?</div>
                Giriş, ödeme, veri görünmeme, rapor indirme, senkron, kamera veya sistem hatalarında “Destek Taleplerim” sekmesinden talep açın. Sorunu hızlandırmak için hangi ekranda olduğunuzu, kovan adını ve mümkünse hata metnini yazın.
            </div>
        </div>
    );
}

function HelpCenterPage() {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [newTicketSubject, setNewTicketSubject] = useState('');
    const [newTicketMessage, setNewTicketMessage] = useState('');
    const [showNewTicketModal, setShowNewTicketModal] = useState(false);
    const messagesEndRef = React.useRef(null);

    const [activeTab, setActiveTab] = useState('guide'); // guide, tickets

    useEffect(() => {
        fetchTickets();
    }, []);

    useEffect(() => {
        if (activeTicket) {
            fetchMessages(activeTicket.id);
            const interval = setInterval(() => fetchMessages(activeTicket.id), 3000);
            return () => clearInterval(interval);
        }
    }, [activeTicket]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const fetchTickets = async () => {
        try {
            const res = await axios.get('/api/support/tickets');
            setTickets(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchMessages = async (id) => {
        try {
            const res = await axios.get(`/api/support/tickets/${id}/messages`);
            setMessages(res.data.data);
        } catch (err) { console.error(err); }
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/support/tickets', { subject: newTicketSubject, message: newTicketMessage });
            setNewTicketSubject('');
            setNewTicketMessage('');
            setShowNewTicketModal(false);
            fetchTickets();
            setActiveTab('tickets'); 
        } catch (err) { alert("Talep oluşturulamadı"); }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        try {
            await axios.post(`/api/support/tickets/${activeTicket.id}/messages`, { message: newMessage });
            setMessages([...messages, { 
                id: Date.now(), 
                ticket_id: activeTicket.id, 
                sender_id: user.id, 
                message: newMessage, 
                created_at: new Date().toISOString(),
                username: user.username 
            }]);
            setNewMessage('');
        } catch (err) { alert("Mesaj gönderilemedi"); }
    };

    const GuideContent = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-amber-500">
                <h3 className="font-bold text-lg text-gray-800 mb-2 flex items-center gap-2">🚀 Başlangıç</h3>
                <p className="text-gray-600 text-sm mb-2">KovanTakip'e hoş geldiniz! Uygulamayı kullanmaya başlamak için önce kovanlarınızı sisteme eklemeniz gerekmektedir.</p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                    <li>Ana sayfadaki <b>"+ Ekle"</b> butonunu kullanarak yeni kovan oluşturun.</li>
                    <li>Her kovana benzersiz bir isim veya numara verin.</li>
                    <li>Kovanlarınızın QR kodlarını yazdırıp kovan üzerine yapıştırın.</li>
                </ul>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-blue-500">
                <h3 className="font-bold text-lg text-gray-800 mb-2 flex items-center gap-2">📷 QR Kod ile Hızlı İşlem</h3>
                <p className="text-gray-600 text-sm mb-2">Kovanların yanına gittiğinizde kovanı aramak yerine QR kodu taratın.</p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                    <li>Alt menüdeki <b>"Tara"</b> butonuna basın.</li>
                    <li>Kamerayı kovanın üzerindeki QR koda tutun.</li>
                    <li>Otomatik olarak o kovanın detay sayfası açılacaktır.</li>
                </ul>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-green-500">
                <h3 className="font-bold text-lg text-gray-800 mb-2 flex items-center gap-2">📝 Bakım ve Hasat Kayıtları</h3>
                <p className="text-gray-600 text-sm mb-2">Kovan detay sayfasında <b>"+ Bakım Ekle"</b> butonunu kullanarak yapılan işlemleri kaydedin.</p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div className="bg-green-50 p-2 rounded text-green-800"><b>Hasat:</b> Bal, Polen, Propolis miktarlarını girin.</div>
                    <div className="bg-blue-50 p-2 rounded text-blue-800"><b>Besleme:</b> Şerbet veya Kek verme işlemlerini kaydedin.</div>
                    <div className="bg-red-50 p-2 rounded text-red-800"><b>İlaçlama:</b> Varroa mücadelesi ve ilaç takibi yapın.</div>
                    <div className="bg-gray-50 p-2 rounded text-gray-800"><b>Notlar:</b> Sesli not veya fotoğraf ekleyin.</div>
                </div>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-purple-500">
                <h3 className="font-bold text-lg text-gray-800 mb-2 flex items-center gap-2">🌤️ Hava Durumu Ayarı</h3>
                <p className="text-gray-600 text-sm">Ana sayfadaki hava durumu widget'ının doğru çalışması için sağ üstteki <b>Profil</b> ikonuna tıklayıp <b>Şehir</b> bilgisini güncelleyin.</p>
            </div>
        </div>
    );

    const renderContent = () => {
        if (activeTab === 'guide') {
            return <UpdatedHelpGuide />;
        }

        if (activeTicket) {
            return (
                <div className="flex flex-col h-[calc(100vh-200px)] bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-4 bg-gray-100 border-b flex items-center justify-between">
                        <button onClick={() => setActiveTicket(null)} className="flex items-center text-gray-600 hover:text-gray-900">
                            <ArrowLeft size={20} className="mr-1"/> Geri
                        </button>
                        <div className="text-center">
                            <h2 className="font-bold text-gray-800 text-sm md:text-base">{activeTicket.subject}</h2>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${
                                activeTicket.status === 'open' ? 'bg-green-100 text-green-800' :
                                activeTicket.status === 'answered' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {activeTicket.status === 'open' ? 'Açık' : activeTicket.status === 'answered' ? 'Yanıtlandı' : 'Kapalı'}
                            </span>
                        </div>
                        <div className="w-16"></div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                        {messages.map(msg => {
                            const isMe = msg.sender_id === user.id;
                            const isAdmin = msg.role === 'admin';
                            return (
                                <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                    <div className={`max-w-[85%] rounded-lg p-3 shadow-sm relative ${
                                        isMe ? 'bg-blue-600 text-white rounded-br-none' : 
                                        isAdmin ? 'bg-amber-100 text-amber-900 border border-amber-200 rounded-bl-none' :
                                        'bg-white text-gray-800 rounded-bl-none'
                                    }`}>
                                        {!isMe && <div className="text-xs font-bold mb-1 opacity-75 flex items-center gap-1">
                                            {isAdmin && <span className="bg-amber-500 text-white text-[9px] px-1 rounded">YÖNETİCİ</span>}
                                            {msg.username}
                                        </div>}
                                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                                        <div className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                                            {parseServerDate(msg.created_at)?.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    <form onSubmit={handleSendMessage} className="p-3 bg-white border-t flex gap-2 items-center">
                        <input 
                            type="text" 
                            className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Mesaj yazın..."
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                        />
                        <button type="submit" className="bg-blue-600 text-white p-2.5 rounded-full hover:bg-blue-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed" disabled={!newMessage.trim()}>
                            <Send size={18} className="ml-0.5" />
                        </button>
                    </form>
                </div>
            );
        }

        return (
            <>
                <div className="mb-4">
                    <button onClick={() => setShowNewTicketModal(true)} className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white p-3 rounded-lg font-bold shadow hover:shadow-lg transition flex items-center justify-center gap-2">
                        <Plus size={20} /> Yeni Destek Talebi Oluştur
                    </button>
                </div>

                <div className="space-y-3 pb-20">
                    {tickets.map(t => (
                        <div key={t.id} onClick={() => setActiveTicket(t)} className="bg-white p-4 rounded-lg shadow-sm cursor-pointer hover:bg-blue-50 border-l-4 border-transparent hover:border-blue-500 transition group">
                            <div className="flex justify-between items-start mb-1">
                                <h3 className="font-bold text-gray-800 group-hover:text-blue-700">{t.subject}</h3>
                                <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${
                                    t.status === 'open' ? 'bg-green-100 text-green-700' :
                                    t.status === 'answered' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-600'
                                }`}>
                                    {t.status === 'open' ? 'Açık' : t.status === 'answered' ? 'Yanıtlandı' : 'Kapalı'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xs text-gray-500">
                                <span>Talep No: #{t.id}</span>
                                <span>{parseServerDate(t.created_at)?.toLocaleDateString('tr-TR')}</span>
                            </div>
                        </div>
                    ))}
                    {tickets.length === 0 && (
                        <div className="text-center py-10 bg-white rounded-lg border border-dashed border-gray-300">
                            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
                                <MessageSquare size={30}/>
                            </div>
                            <p className="text-gray-500 font-medium">Henüz bir destek talebiniz yok.</p>
                            <p className="text-xs text-gray-400 mt-1">Sorularınız için yukarıdaki butonu kullanın.</p>
                        </div>
                    )}
                </div>

                {showNewTicketModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                            <h3 className="text-lg font-bold mb-4">Yeni Destek Talebi</h3>
                            <form onSubmit={(e) => {
                                handleCreateTicket(e);
                                setShowNewTicketModal(false);
                            }} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Konu</label>
                                    <input 
                                        type="text" className="w-full border rounded p-2 mt-1"
                                        value={newTicketSubject} onChange={e => setNewTicketSubject(e.target.value)}
                                        placeholder="Örn: Kovan ekleyemiyorum"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Mesaj</label>
                                    <textarea 
                                        className="w-full border rounded p-2 mt-1" rows="4"
                                        value={newTicketMessage} onChange={e => setNewTicketMessage(e.target.value)}
                                        placeholder="Sorununuzu detaylı anlatın..."
                                        required
                                    ></textarea>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button type="button" onClick={() => setShowNewTicketModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200">İptal</button>
                                    <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700">Gönder</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <Layout title="Yardım Merkezi">
            <div className="flex bg-white rounded-lg shadow-sm mb-4 p-1">
                <button 
                    onClick={() => setActiveTab('guide')}
                    className={`flex-1 py-2 rounded-md text-sm font-bold transition ${activeTab === 'guide' ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    📖 Kullanım Rehberi
                </button>
                <button 
                    onClick={() => setActiveTab('tickets')}
                    className={`flex-1 py-2 rounded-md text-sm font-bold transition ${activeTab === 'tickets' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    📩 Destek Taleplerim
                </button>
            </div>

            {renderContent()}
        </Layout>
    );
}

function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <div className="flex items-center justify-center min-h-screen">Yükleniyor...</div>;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
}

function App() {
  return (
    <AuthProvider>
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            <Route path="/" element={
                <RequireAuth>
                    <Dashboard />
                </RequireAuth>
            } />
            <Route path="/admin" element={
                <RequireAuth>
                    <AdminPanel />
                </RequireAuth>
            } />
            <Route path="/chat" element={
                <RequireAuth>
                    <CommunityChat />
                </RequireAuth>
            } />
            <Route path="/handbook" element={
                <RequireAuth>
                    <BeekeeperHandbookV2 />
                </RequireAuth>
            } />
            <Route path="/tools" element={
                <RequireAuth>
                    <ToolsHub />
                </RequireAuth>
            } />
            <Route path="/ai-analysis" element={
                <RequireAuth>
                    <AiAnalysis />
                </RequireAuth>
            } />
            <Route path="/help" element={
                <RequireAuth>
                    <HelpCenterPage />
                </RequireAuth>
            } />
            <Route path="/new-hive" element={
                <RequireAuth>
                    <NewHive />
                </RequireAuth>
            } />
            <Route path="/hive/:id" element={
                <RequireAuth>
                    <HiveDetail />
                </RequireAuth>
            } />
            <Route path="/hive/:id/log" element={
                <RequireAuth>
                    <AddLog />
                </RequireAuth>
            } />
            <Route path="/scan" element={
                <RequireAuth>
                    <QRScan />
                </RequireAuth>
            } />
        </Routes>
    </AuthProvider>
  );
}

export default App;
